import axios from 'axios'
import { useEffect, useRef, useState } from 'react'

const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined) || '/api'
const SESSION_KEY = 'adminSecret'

function getSecret() { return sessionStorage.getItem(SESSION_KEY) ?? '' }

function adminApi() {
  return axios.create({
    baseURL: BASE_URL,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getSecret()}` },
  })
}

function apiError(err: unknown): string {
  const e = err as { response?: { status?: number; data?: { error?: { message?: string }; message?: string } }; message?: string }
  const msg = e?.response?.data?.error?.message ?? e?.response?.data?.message ?? e?.message ?? 'Ошибка запроса'
  const status = e?.response?.status
  return status ? `[${status}] ${msg}` : msg
}

function fmtDate(d: string) {
  return new Date(d).toLocaleDateString('ru', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function daysLeft(expiresAt: string | null): number | null {
  if (!expiresAt) return null
  return Math.ceil((new Date(expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
}

type TenantStatus = 'active' | 'trial' | 'paused' | 'blocked'
type IncidentSeverity = 'low' | 'medium' | 'high' | 'critical'
type IncidentStatus = 'open' | 'investigating' | 'resolved' | 'closed'
type IncidentCategory = 'integration' | 'performance' | 'data' | 'billing' | 'security' | 'other'

interface Owner { id: string; name: string; phone: string; isActive: boolean }
interface Tenant {
  id: string; name: string; plan: string; createdAt: string
  status: TenantStatus; statusReason?: string | null
  limits?: Record<string, unknown> | null
  lastActivityAt?: string | null
  isDemoTenant: boolean; isDisabled: boolean; expiresAt: string | null
  _count: { users: number; bikes: number; rentals: number; incidents: number }
  users: Owner[]
}
interface User {
  id: string; name: string; phone: string; role: 'owner' | 'manager'
  isActive: boolean; lastLoginAt?: string; createdAt: string
}

interface DashboardStats {
  status: { active: number; trial: number; paused: number; blocked: number; total: number }
  openIncidents: number
  noisyTenants: Array<{
    tenantId: string
    tenant?: { id: string; name: string; plan: string; status: TenantStatus }
    rentals: number; errors: number; revenue: number
  }>
}

interface Incident {
  id: string; tenantId: string; title: string; description?: string | null
  severity: IncidentSeverity; category: IncidentCategory; status: IncidentStatus
  resolvedAt?: string | null; createdAt: string; updatedAt: string
  tenant?: { id: string; name: string; plan: string; status: TenantStatus }
}

interface UsageEntry {
  tenantId: string; name: string; plan: string; status: TenantStatus
  rentals: number; shifts: number; payments: number; errors: number; revenue: number
}

interface TenantDetails {
  tenant: Tenant & { contactPhone?: string | null; timezone: string }
  healthScore: number
  usage: {
    last7d: { rentals: number; shifts: number; payments: number; errors: number; revenue: number }
    snapshots7d: Array<{ date: string; rentalsCount: number; errorsCount: number; revenueTotal: string }>
  }
  recentAuditLogs: Array<{
    id: number; action: string; entityType?: string; entityId?: string
    createdAt: string; reason?: string; userId?: string
  }>
  openIncidents: Array<{ id: string; title: string; severity: IncidentSeverity; status: IncidentStatus; createdAt: string }>
}

interface AdminAuditEntry {
  id: number; adminKey?: string; action: string; targetType?: string; targetId?: string
  oldValue?: unknown; newValue?: unknown; reason?: string; createdAt: string
}

function fmtNum(n: number) { return n.toLocaleString('ru') }

function StatusBadge({ status }: { status: TenantStatus }) {
  const cfg: Record<TenantStatus, { label: string; cls: string }> = {
    active: { label: 'активен', cls: 'bg-green-100 text-green-700' },
    trial: { label: 'пробный', cls: 'bg-blue-100 text-blue-700' },
    paused: { label: 'на паузе', cls: 'bg-yellow-100 text-yellow-700' },
    blocked: { label: 'заблокирован', cls: 'bg-red-100 text-red-700' },
  }
  const { label, cls } = cfg[status] ?? cfg.active
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cls}`}>{label}</span>
}

function SeverityBadge({ severity }: { severity: IncidentSeverity }) {
  const cfg: Record<IncidentSeverity, { label: string; cls: string }> = {
    low: { label: 'низкий', cls: 'bg-gray-100 text-gray-600' },
    medium: { label: 'средний', cls: 'bg-yellow-100 text-yellow-700' },
    high: { label: 'высокий', cls: 'bg-orange-100 text-orange-700' },
    critical: { label: 'критичный', cls: 'bg-red-100 text-red-700' },
  }
  const { label, cls } = cfg[severity] ?? cfg.medium
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cls}`}>{label}</span>
}

// ─── Login ───────────────────────────────────────────────────────────────────

function LoginScreen({ onLogin }: { onLogin: () => void }) {
  const ref = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const secret = ref.current?.value?.trim() ?? ''
    if (!secret) { setError('Введите секрет'); return }
    setLoading(true); setError(null)
    try {
      sessionStorage.setItem(SESSION_KEY, secret)
      await adminApi().get('/admin/tenants')
      onLogin()
    } catch (err) {
      sessionStorage.removeItem(SESSION_KEY)
      const e = err as { response?: { status?: number } }
      if (e?.response?.status === 401) setError('Неверный секрет')
      else if (e?.response?.status === 503) setError('ADMIN_SECRET не настроен на сервере')
      else setError('Ошибка соединения с сервером')
    } finally { setLoading(false) }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-6">
          <div className="text-4xl mb-2">🔧</div>
          <h1 className="text-xl font-bold text-gray-900">VeloRent Admin</h1>
          <p className="text-xs text-gray-500 mt-1">Управление тенантами</p>
        </div>
        {error && <div className="mb-3 bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">{error}</div>}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Admin Secret</label>
              <input ref={ref} type="password" placeholder="Введите ADMIN_SECRET"
                className="w-full rounded-xl border-gray-300 shadow-sm text-base" autoFocus />
            </div>
            <button type="submit" disabled={loading}
              className="w-full bg-gray-900 hover:bg-gray-800 disabled:opacity-60 text-white font-semibold py-3 rounded-xl transition-colors">
              {loading ? 'Проверка...' : 'Войти'}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}

// ─── Cheat sheet ─────────────────────────────────────────────────────────────

const COMMANDS = [
  {
    section: 'Управление тенантами',
    items: [
      {
        label: 'Список тенантов',
        cmd: `docker exec velorent_api node -e "
const{PrismaClient}=require('@prisma/client');const p=new PrismaClient();
p.tenant.findMany({select:{id:true,name:true,isDisabled:true,createdAt:true}})
  .then(ts=>{console.log(JSON.stringify(ts,null,2));return p.\\$disconnect()});"`
      },
      {
        label: 'Создать тенанта (изменить данные)',
        cmd: `docker exec velorent_api node -e "
const{PrismaClient}=require('@prisma/client');const bcrypt=require('bcrypt');const p=new PrismaClient();
(async()=>{
  const hash=await bcrypt.hash('ПАРОЛЬ',12);
  const t=await p.tenant.create({data:{name:'НАЗВАНИЕ',plan:'base',
    users:{create:{name:'ВЛАДЕЛЕЦ',phone:'+7XXXXXXXXXX',normalizedPhone:'+7XXXXXXXXXX',passwordHash:hash,role:'owner'}}},
    select:{id:true,name:true}});
  console.log('Создан:',JSON.stringify(t));await p.\\$disconnect();
})().catch(e=>{console.error(e.message);process.exit(1)});"`
      },
      {
        label: 'Заблокировать тенанта по ID',
        cmd: `docker exec velorent_api node -e "
const{PrismaClient}=require('@prisma/client');const p=new PrismaClient();
p.tenant.update({where:{id:'TENANT_ID'},data:{isDisabled:true}})
  .then(t=>{console.log('Заблокирован:',t.id);return p.\\$disconnect()});"`
      },
      {
        label: 'Разблокировать тенанта по ID',
        cmd: `docker exec velorent_api node -e "
const{PrismaClient}=require('@prisma/client');const p=new PrismaClient();
p.tenant.update({where:{id:'TENANT_ID'},data:{isDisabled:false}})
  .then(t=>{console.log('Разблокирован:',t.id);return p.\\$disconnect()});"`
      },
    ]
  },
  {
    section: 'Управление пользователями',
    items: [
      {
        label: 'Список пользователей тенанта',
        cmd: `docker exec velorent_api node -e "
const{PrismaClient}=require('@prisma/client');const p=new PrismaClient();
p.user.findMany({where:{tenantId:'TENANT_ID'},select:{id:true,name:true,phone:true,role:true,isActive:true}})
  .then(us=>{console.log(JSON.stringify(us,null,2));return p.\\$disconnect()});"`
      },
      {
        label: 'Добавить менеджера в тенанта',
        cmd: `docker exec velorent_api node -e "
const{PrismaClient}=require('@prisma/client');const bcrypt=require('bcrypt');const p=new PrismaClient();
(async()=>{
  const hash=await bcrypt.hash('ПАРОЛЬ',12);
  const u=await p.user.create({data:{tenantId:'TENANT_ID',name:'ИМЯ',phone:'+7XXXXXXXXXX',
    normalizedPhone:'+7XXXXXXXXXX',passwordHash:hash,role:'manager'}});
  console.log('Создан:',u.id);await p.\\$disconnect();
})().catch(e=>{console.error(e.message);process.exit(1)});"`
      },
      {
        label: 'Сбросить пароль владельца',
        cmd: `docker exec velorent_api node -e "
const{PrismaClient}=require('@prisma/client');const bcrypt=require('bcrypt');const p=new PrismaClient();
(async()=>{
  const hash=await bcrypt.hash('НОВЫЙ_ПАРОЛЬ',12);
  await p.user.updateMany({where:{tenantId:'TENANT_ID',role:'owner'},data:{passwordHash:hash}});
  console.log('Пароль сброшен');await p.\\$disconnect();
})();"`
      },
      {
        label: 'Деактивировать пользователя по ID',
        cmd: `docker exec velorent_api node -e "
const{PrismaClient}=require('@prisma/client');const p=new PrismaClient();
p.user.update({where:{id:'USER_ID'},data:{isActive:false}})
  .then(u=>{console.log('Деактивирован:',u.name);return p.\\$disconnect()});"`
      },
      {
        label: 'Удалить менеджера по ID',
        cmd: `docker exec velorent_api node -e "
const{PrismaClient}=require('@prisma/client');const p=new PrismaClient();
(async()=>{
  await p.telegramLink.deleteMany({where:{userId:'USER_ID'}});
  await p.telegramLinkCode.deleteMany({where:{userId:'USER_ID'}});
  await p.user.delete({where:{id:'USER_ID'}});
  console.log('Удалён');await p.\\$disconnect();
})().catch(e=>{console.error(e.message);process.exit(1)});"`
      },
    ]
  },
  {
    section: 'Диагностика',
    items: [
      { label: 'Логи API (последние 100 строк)', cmd: 'docker logs velorent_api --tail=100' },
      { label: 'Логи API (live)', cmd: 'docker logs velorent_api -f --tail=50' },
      { label: 'Статус контейнеров', cmd: 'docker ps' },
      { label: 'Проверка здоровья API', cmd: 'curl -s http://localhost:3001/health/ready' },
      { label: 'Подключиться к psql', cmd: 'docker exec -it velorent_postgres psql -U velorent -d velorent' },
    ]
  },
  {
    section: 'Деплой',
    items: [
      {
        label: 'Перейти в папку проекта',
        cmd: 'cd /opt/velorent'
      },
      {
        label: 'Обновить код и пересобрать всё',
        cmd: 'cd /opt/velorent && git pull && docker compose -f docker-compose.prod.yml build && docker compose -f docker-compose.prod.yml up -d'
      },
      {
        label: 'Пересобрать только API (быстрее)',
        cmd: 'cd /opt/velorent && git pull && docker compose -f docker-compose.prod.yml build api && docker compose -f docker-compose.prod.yml up -d api'
      },
      {
        label: 'Применить миграции вручную',
        cmd: 'docker exec velorent_api npx prisma migrate deploy'
      },
      {
        label: 'Посмотреть ADMIN_SECRET',
        cmd: 'grep ADMIN_SECRET /opt/velorent/.env'
      },
    ]
  },
]

// ─── Diagnostics Tab ──────────────────────────────────────────────────────────

interface DiagCheck { ok: boolean; error?: string; pending?: string[] }
interface DiagResult {
  ok: boolean
  timestamp: string
  checks: { database: DiagCheck; redis: DiagCheck; migrations: DiagCheck }
}

const ERROR_HINTS: Array<{ match: string; title: string; why: string; fix?: string }> = [
  {
    match: 'FST_ERR_PLUGIN_VERSION_MISMATCH',
    title: 'Конфликт версий плагина Fastify',
    why: 'Установлена версия плагина, несовместимая с текущей версией Fastify. Такое бывает после обновления пакетов.',
    fix: 'В коде поправить версию плагина в package.json и задеплоить заново.',
  },
  {
    match: 'Cannot find module',
    title: 'Не найден модуль',
    why: 'Зависимости не установились при сборке. Обычно помогает пересборка.',
    fix: 'Пересобрать Docker-образ (команда ниже в шпаргалке).',
  },
  {
    match: 'PrismaClientInitializationError',
    title: 'База данных не доступна',
    why: 'API не может подключиться к PostgreSQL. Контейнер с базой упал или DATABASE_URL неправильный.',
    fix: 'Проверить docker ps — база должна быть healthy.',
  },
  {
    match: 'Do not know how to serialize',
    title: 'Ошибка сериализации данных',
    why: 'API пытается отдать тип данных, который JSON не понимает (BigInt, Decimal). Нужен патч кода.',
  },
  {
    match: 'migration',
    title: 'Проблема с миграциями',
    why: 'База данных не совпадает со схемой. Нужно применить миграции кнопкой ниже.',
  },
]

function findHint(error: string) {
  return ERROR_HINTS.find(h => error.toLowerCase().includes(h.match.toLowerCase()))
}

function StatusDot({ ok, loading }: { ok?: boolean; loading?: boolean }) {
  if (loading) return <span className="inline-block w-2.5 h-2.5 rounded-full bg-gray-300 animate-pulse" />
  return <span className={`inline-block w-2.5 h-2.5 rounded-full ${ok ? 'bg-green-500' : 'bg-red-500'}`} />
}

function DiagnosticsTab() {
  const [diag, setDiag] = useState<DiagResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [migrateLoading, setMigrateLoading] = useState(false)
  const [migrateOutput, setMigrateOutput] = useState<string | null>(null)
  const [migrateError, setMigrateError] = useState<string | null>(null)
  const [copied, setCopied] = useState<string | null>(null)
  const [showGuide, setShowGuide] = useState(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const fetchDiag = () => {
    adminApi().get<DiagResult>('/admin/diagnostics')
      .then(r => { setDiag(r.data); setLoading(false) })
      .catch(() => setLoading(false))
  }

  useEffect(() => {
    fetchDiag()
    intervalRef.current = setInterval(fetchDiag, 30_000)
    return () => { if (intervalRef.current) clearInterval(intervalRef.current) }
  }, [])

  const runMigrate = async () => {
    setMigrateLoading(true); setMigrateOutput(null); setMigrateError(null)
    try {
      const r = await adminApi().post<{ ok: boolean; output: string }>('/admin/actions/migrate')
      setMigrateOutput(r.data.output)
      fetchDiag()
    } catch (err) {
      const e = err as { response?: { data?: { output?: string; error?: string } } }
      setMigrateError(e?.response?.data?.error ?? 'Неизвестная ошибка')
      setMigrateOutput(e?.response?.data?.output ?? null)
    } finally { setMigrateLoading(false) }
  }

  const copy = (text: string, key: string) => {
    navigator.clipboard.writeText(text).then(() => { setCopied(key); setTimeout(() => setCopied(null), 1500) })
  }

  const checks = diag?.checks
  const pendingMigrations = checks?.migrations?.pending ?? []
  const hasProblem = diag && !diag.ok

  return (
    <div className="p-4 max-w-2xl mx-auto space-y-4">

      {/* Watchdog banner */}
      {hasProblem && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
          <p className="font-semibold text-red-800 text-sm mb-1">Обнаружена проблема</p>
          <p className="text-xs text-red-600">
            Один или несколько компонентов не в порядке. Смотри детали ниже.
          </p>
        </div>
      )}

      {/* Status panel */}
      <div className="bg-white border border-gray-200 rounded-2xl p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-gray-800 text-sm">Состояние системы</h2>
          <div className="flex items-center gap-2">
            {diag && <span className="text-xs text-gray-400">обновлено {new Date(diag.timestamp).toLocaleTimeString('ru')}</span>}
            <button onClick={() => { setLoading(true); fetchDiag() }}
              className="text-xs border border-gray-300 px-2 py-1 rounded-lg hover:bg-gray-50">
              Обновить
            </button>
          </div>
        </div>

        {[
          { key: 'database', label: 'База данных (PostgreSQL)', check: checks?.database },
          { key: 'redis', label: 'Redis (очереди / кэш)', check: checks?.redis },
          { key: 'migrations', label: 'Миграции базы данных', check: checks?.migrations },
        ].map(({ key, label, check }) => (
          <div key={key} className="flex items-start gap-3">
            <div className="mt-0.5"><StatusDot ok={check?.ok} loading={loading} /></div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-700">{label}</p>
              {check && !check.ok && (
                <p className="text-xs text-red-600 mt-0.5">
                  {check.error ?? (key === 'migrations' && pendingMigrations.length > 0
                    ? `Не применено: ${pendingMigrations.join(', ')}`
                    : 'Недоступно')}
                </p>
              )}
              {check?.ok && key === 'migrations' && (
                <p className="text-xs text-green-600 mt-0.5">Все миграции применены</p>
              )}
            </div>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
              loading ? 'bg-gray-100 text-gray-400'
              : check?.ok ? 'bg-green-100 text-green-700'
              : 'bg-red-100 text-red-700'
            }`}>
              {loading ? '...' : check?.ok ? 'OK' : 'ОШИБКА'}
            </span>
          </div>
        ))}
      </div>

      {/* Migrate action */}
      {(pendingMigrations.length > 0 || !checks?.migrations?.ok) && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
          <p className="text-sm font-semibold text-amber-800 mb-1">Есть незакрытые миграции</p>
          <p className="text-xs text-amber-700 mb-3">
            База данных отстаёт от кода — нужно применить обновления схемы. Нажми кнопку, она сделает это сама.
          </p>
          <button onClick={runMigrate} disabled={migrateLoading}
            className="text-sm bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white px-4 py-2 rounded-xl font-medium">
            {migrateLoading ? 'Применяю...' : 'Применить миграции'}
          </button>
          {migrateOutput && (
            <pre className="mt-3 text-xs bg-gray-900 text-green-300 rounded-xl p-3 max-h-40 overflow-y-auto whitespace-pre-wrap">
              {migrateOutput}
            </pre>
          )}
          {migrateError && <p className="mt-2 text-xs text-red-700">{migrateError}</p>}
        </div>
      )}

      {/* Always-available migrate button */}
      {checks?.migrations?.ok && (
        <div className="bg-white border border-gray-200 rounded-2xl p-4">
          <p className="text-sm font-semibold text-gray-700 mb-1">Применить миграции</p>
          <p className="text-xs text-gray-500 mb-3">Нужно запускать после деплоя, если добавлялись изменения в базу данных.</p>
          <button onClick={runMigrate} disabled={migrateLoading}
            className="text-sm border border-gray-300 px-4 py-2 rounded-xl hover:bg-gray-50 disabled:opacity-50">
            {migrateLoading ? 'Применяю...' : 'Запустить migrate deploy'}
          </button>
          {migrateOutput && (
            <pre className="mt-3 text-xs bg-gray-900 text-green-300 rounded-xl p-3 max-h-40 overflow-y-auto whitespace-pre-wrap">
              {migrateOutput}
            </pre>
          )}
        </div>
      )}

      {/* Troubleshooting guide */}
      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
        <button onClick={() => setShowGuide(g => !g)}
          className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50">
          <span className="font-semibold text-gray-800 text-sm">Инструкция: что делать если сломалось</span>
          <span className="text-gray-400 text-lg">{showGuide ? '−' : '+'}</span>
        </button>
        {showGuide && (
          <div className="border-t border-gray-100 p-4 space-y-4 text-sm">

            <div className="space-y-1">
              <p className="font-semibold text-gray-700">Правило #1 — всегда показывай логи Cursor-у</p>
              <p className="text-gray-500 text-xs">
                "Что-то сломалось" — это не ошибка. Ошибка — это текст из логов. Без текста чинить нечего.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-3 space-y-2">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Сценарий: API не отвечает (сайт не открывается)</p>
              <p className="text-xs text-gray-500">1. Зайди по SSH на сервер</p>
              <p className="text-xs text-gray-500">2. Запусти команду из шпаргалки "Статус контейнеров"</p>
              <p className="text-xs text-gray-500">3. Скинь вывод Cursor-у с фразой "вот логи, почини"</p>
            </div>

            <div className="bg-gray-50 rounded-xl p-3 space-y-2">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Сценарий: API падает сразу после деплоя</p>
              <p className="text-xs text-gray-500">1. Запусти "Логи API" из шпаргалки ниже</p>
              <p className="text-xs text-gray-500">2. Скинь последние 50 строк Cursor-у</p>
            </div>

            <div className="bg-gray-50 rounded-xl p-3 space-y-2">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Сценарий: Эндпоинт возвращает 500</p>
              <p className="text-xs text-gray-500">1. Открой логи API — там будет строка с ошибкой</p>
              <p className="text-xs text-gray-500">2. Покажи Cursor-у: "вот 500, вот логи"</p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
              <p className="text-xs font-semibold text-amber-800 mb-1">Что такое "миграции"?</p>
              <p className="text-xs text-amber-700">
                Когда мы добавляем новые таблицы или поля в базу данных — нужно "применить миграции".
                Это как установка обновления для базы. Без этого API будет падать с ошибкой о несуществующей таблице.
                Кнопка выше делает это автоматически.
              </p>
            </div>

            <div className="space-y-1">
              <p className="font-semibold text-gray-700">Известные ошибки и что они значат</p>
              {ERROR_HINTS.map(h => (
                <div key={h.match} className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs font-mono text-red-700 mb-1">{h.match}</p>
                  <p className="text-xs font-medium text-gray-700">{h.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{h.why}</p>
                  {h.fix && <p className="text-xs text-blue-700 mt-1">→ {h.fix}</p>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* SSH Cheatsheet */}
      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
        <div className="p-4 border-b border-gray-100">
          <p className="font-semibold text-gray-800 text-sm">Шпаргалка — команды для сервера</p>
          <p className="text-xs text-gray-400 mt-0.5">
            SSH: <code className="bg-gray-100 px-1 rounded">ssh root@213.171.6.180</code> — потом вставляй команды там
          </p>
        </div>
        <div className="p-4 space-y-5">
          {COMMANDS.map((section) => (
            <div key={section.section}>
              <h3 className="font-semibold text-gray-600 mb-2 text-xs uppercase tracking-wide">{section.section}</h3>
              <div className="space-y-2">
                {section.items.map((item, i) => {
                  const key = `${section.section}-${i}`
                  return (
                    <div key={key} className="bg-gray-50 rounded-xl p-3 flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 mb-1">{item.label}</p>
                        <pre className="text-xs text-gray-500 whitespace-pre-wrap break-all leading-relaxed font-mono max-h-20 overflow-y-auto">
                          {item.cmd.trim()}
                        </pre>
                      </div>
                      <button onClick={() => copy(item.cmd, key)}
                        className="shrink-0 text-xs border border-gray-300 px-2 py-1 rounded-lg hover:bg-white whitespace-nowrap mt-0.5">
                        {copied === key ? '✓' : 'Копировать'}
                      </button>
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function CheatSheet() {
  const [copied, setCopied] = useState<string | null>(null)

  const copy = (cmd: string, key: string) => {
    navigator.clipboard.writeText(cmd).then(() => {
      setCopied(key)
      setTimeout(() => setCopied(null), 1500)
    })
  }

  return (
    <div className="p-4 max-w-2xl mx-auto space-y-6">
      <p className="text-xs text-gray-500">
        SSH: <code className="bg-gray-100 px-1 rounded">ssh root@213.171.6.180</code>
        &nbsp;— выполняй команды там. Нажми кнопку справа чтобы скопировать.
      </p>
      {COMMANDS.map((section) => (
        <div key={section.section}>
          <h3 className="font-semibold text-gray-700 mb-2 text-sm">{section.section}</h3>
          <div className="space-y-2">
            {section.items.map((item, i) => {
              const key = `${section.section}-${i}`
              return (
                <div key={key} className="bg-white border border-gray-200 rounded-xl p-3 flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 mb-1">{item.label}</p>
                    <pre className="text-xs text-gray-500 whitespace-pre-wrap break-all leading-relaxed font-mono bg-gray-50 rounded-lg p-2 max-h-24 overflow-y-auto">
                      {item.cmd.trim()}
                    </pre>
                  </div>
                  <button
                    onClick={() => copy(item.cmd, key)}
                    className="shrink-0 text-xs border border-gray-300 px-2 py-1 rounded-lg hover:bg-gray-50 whitespace-nowrap mt-0.5"
                  >
                    {copied === key ? '✓' : 'Копировать'}
                  </button>
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

// ─── Modals ───────────────────────────────────────────────────────────────────

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-lg">{title}</h3>
          <button onClick={onClose} className="text-gray-400 text-3xl leading-none hover:text-gray-600">×</button>
        </div>
        {children}
      </div>
    </div>
  )
}

function CreateTenantModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const nameRef = useRef<HTMLInputElement>(null)
  const ownerNameRef = useRef<HTMLInputElement>(null)
  const phoneRef = useRef<HTMLInputElement>(null)
  const passwordRef = useRef<HTMLInputElement>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setError(null)
    const tenantName = nameRef.current?.value?.trim() ?? ''
    const ownerName = ownerNameRef.current?.value?.trim() ?? ''
    const ownerPhone = phoneRef.current?.value?.trim() ?? ''
    const ownerPassword = passwordRef.current?.value ?? ''
    if (!tenantName) { setError('Введите название'); return }
    if (!ownerName) { setError('Введите имя владельца'); return }
    if (!ownerPhone) { setError('Введите телефон'); return }
    if (ownerPassword.length < 6) { setError('Пароль — минимум 6 символов'); return }
    setLoading(true)
    try {
      await adminApi().post('/admin/tenants', { tenantName, ownerName, ownerPhone, ownerPassword })
      onCreated(); onClose()
    } catch (err) { setError(apiError(err)) } finally { setLoading(false) }
  }

  return (
    <Modal title="Новый тенант" onClose={onClose}>
      {error && <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">{error}</div>}
      <form onSubmit={handleSubmit} className="space-y-3" autoComplete="off">
        {[
          { ref: nameRef, label: 'Название проката', placeholder: 'Велопрокат Центр' },
          { ref: ownerNameRef, label: 'Имя владельца', placeholder: 'Иван Иванов' },
          { ref: phoneRef, label: 'Телефон владельца', placeholder: '+79001234567', type: 'tel' },
          { ref: passwordRef, label: 'Пароль', placeholder: 'Минимум 6 символов', type: 'password' },
        ].map(({ ref, label, placeholder, type }) => (
          <div key={label}>
            <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
            <input ref={ref} type={type ?? 'text'} placeholder={placeholder}
              className="w-full rounded-xl border-gray-300 text-sm" autoComplete="off" />
          </div>
        ))}
        <div className="flex gap-2 pt-1">
          <button type="submit" disabled={loading}
            className="flex-1 bg-gray-900 text-white py-3 rounded-xl text-sm font-semibold disabled:opacity-60">
            {loading ? 'Создание...' : 'Создать'}
          </button>
          <button type="button" onClick={onClose} className="flex-1 border border-gray-300 py-3 rounded-xl text-sm">Отмена</button>
        </div>
      </form>
    </Modal>
  )
}

function CreateDemoTenantModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [creds, setCreds] = useState<{ phone: string; password: string; expiresAt: string } | null>(null)
  const [copied, setCopied] = useState(false)
  const nameRef = useRef<HTMLInputElement>(null)
  const ownerNameRef = useRef<HTMLInputElement>(null)
  const phoneRef = useRef<HTMLInputElement>(null)
  const daysRef = useRef<HTMLInputElement>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setError(null)
    const tenantName = nameRef.current?.value?.trim() ?? ''
    const ownerName = ownerNameRef.current?.value?.trim() ?? ''
    const ownerPhone = phoneRef.current?.value?.trim() ?? ''
    const durationDays = parseInt(daysRef.current?.value ?? '3', 10) || 3
    if (!tenantName) { setError('Введите название'); return }
    if (!ownerName) { setError('Введите имя владельца'); return }
    if (!ownerPhone) { setError('Введите телефон'); return }
    setLoading(true)
    try {
      const r = await adminApi().post('/admin/tenants/demo', { tenantName, ownerName, ownerPhone, durationDays })
      setCreds({ phone: r.data.credentials.phone, password: r.data.credentials.password, expiresAt: r.data.tenant.expiresAt })
      onCreated()
    } catch (err) { setError(apiError(err)) } finally { setLoading(false) }
  }

  const copy = () => {
    if (!creds) return
    navigator.clipboard.writeText(`${creds.phone}\n${creds.password}`)
    setCopied(true); setTimeout(() => setCopied(false), 1500)
  }

  if (creds) {
    return (
      <Modal title="Демо-тенант создан" onClose={onClose}>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">
          Доступ действует до <strong>{fmtDate(creds.expiresAt)}</strong>. После истечения все данные удалятся автоматически.
        </div>
        <div className="bg-gray-50 rounded-xl p-4 space-y-2">
          <div className="flex justify-between items-center text-sm">
            <span className="text-gray-500">Телефон:</span>
            <span className="font-mono font-semibold">{creds.phone}</span>
          </div>
          <div className="flex justify-between items-center text-sm">
            <span className="text-gray-500">Пароль:</span>
            <span className="font-mono font-semibold text-brand-700">{creds.password}</span>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => window.open('/login', '_blank')}
            className="flex-1 bg-gray-900 text-white py-3 rounded-xl text-sm font-semibold">
            Открыть /login
          </button>
          <button onClick={copy}
            className="flex-1 border border-gray-300 py-3 rounded-xl text-sm">
            {copied ? '✓ Скопировано' : 'Копировать'}
          </button>
        </div>
      </Modal>
    )
  }

  return (
    <Modal title="Создать демо-тенант" onClose={onClose}>
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">
        Пароль генерируется автоматически. После истечения срока все данные удалятся без предупреждения.
      </div>
      {error && <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">{error}</div>}
      <form onSubmit={handleSubmit} className="space-y-3" autoComplete="off">
        {[
          { ref: nameRef, label: 'Название проката', placeholder: 'Велопрокат Центр' },
          { ref: ownerNameRef, label: 'Имя владельца', placeholder: 'Иван Иванов' },
          { ref: phoneRef, label: 'Телефон владельца', placeholder: '+79001234567', type: 'tel' },
        ].map(({ ref, label, placeholder, type }) => (
          <div key={label}>
            <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
            <input ref={ref} type={type ?? 'text'} placeholder={placeholder}
              className="w-full rounded-xl border-gray-300 text-sm" autoComplete="off" />
          </div>
        ))}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Срок доступа (дней)</label>
          <input ref={daysRef} type="number" defaultValue="3" min="1" max="30"
            className="w-full rounded-xl border-gray-300 text-sm" />
        </div>
        <div className="flex gap-2 pt-1">
          <button type="submit" disabled={loading}
            className="flex-1 bg-amber-500 hover:bg-amber-600 text-white py-3 rounded-xl text-sm font-semibold disabled:opacity-60">
            {loading ? 'Создание...' : 'Создать демо'}
          </button>
          <button type="button" onClick={onClose} className="flex-1 border border-gray-300 py-3 rounded-xl text-sm">Отмена</button>
        </div>
      </form>
    </Modal>
  )
}

function EditOwnerModal({ tenant, onClose, onSaved }: { tenant: Tenant; onClose: () => void; onSaved: () => void }) {
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const owner = tenant.users[0]
  const nameRef = useRef<HTMLInputElement>(null)
  const phoneRef = useRef<HTMLInputElement>(null)
  const passwordRef = useRef<HTMLInputElement>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setError(null)
    const name = nameRef.current?.value?.trim() ?? ''
    const phone = phoneRef.current?.value?.trim() ?? ''
    const password = passwordRef.current?.value ?? ''
    if (password && password.length < 6) { setError('Пароль — минимум 6 символов'); return }
    if (!name && !phone && !password) { setError('Заполните хотя бы одно поле'); return }
    setLoading(true)
    try {
      await adminApi().patch(`/admin/tenants/${tenant.id}/owner`, {
        ...(name && { name }), ...(phone && { phone }), ...(password && { password }),
      })
      setSuccess(true); onSaved(); setTimeout(onClose, 800)
    } catch (err) { setError(apiError(err)) } finally { setLoading(false) }
  }

  return (
    <Modal title="Редактировать владельца" onClose={onClose}>
      <p className="text-sm text-gray-500">Тенант: <span className="font-medium text-gray-800">{tenant.name}</span></p>
      {error && <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">{error}</div>}
      {success && <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-sm text-green-700">Сохранено</div>}
      <form onSubmit={handleSubmit} className="space-y-3" autoComplete="off">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Имя (пусто — не менять)</label>
          <input ref={nameRef} defaultValue={owner?.name} className="w-full rounded-xl border-gray-300 text-sm" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Телефон (пусто — не менять)</label>
          <input ref={phoneRef} type="tel" defaultValue={owner?.phone} className="w-full rounded-xl border-gray-300 text-sm" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Новый пароль (пусто — не менять)</label>
          <input ref={passwordRef} type="password" placeholder="Минимум 6 символов" className="w-full rounded-xl border-gray-300 text-sm" autoComplete="new-password" />
        </div>
        <div className="flex gap-2 pt-1">
          <button type="submit" disabled={loading || success}
            className="flex-1 bg-gray-900 text-white py-3 rounded-xl text-sm font-semibold disabled:opacity-60">
            {loading ? 'Сохранение...' : 'Сохранить'}
          </button>
          <button type="button" onClick={onClose} className="flex-1 border border-gray-300 py-3 rounded-xl text-sm">Отмена</button>
        </div>
      </form>
    </Modal>
  )
}

interface TempAccessData {
  tempToken: string
  user: { id: string; name: string; phone: string; role: string; tenantId: string }
  wasDisabled: boolean
}

// btoa() breaks on Cyrillic — use encodeURIComponent wrapper
function safeB64Encode(str: string): string {
  return btoa(encodeURIComponent(str))
}

function TempAccessModal({ tenant, onClose }: { tenant: Tenant; onClose: () => void }) {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<TempAccessData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [opened, setOpened] = useState(false)

  const fetch = () => {
    setLoading(true); setError(null)
    adminApi().post(`/admin/tenants/${tenant.id}/temp-access`)
      .then(r => setData(r.data))
      .catch(err => setError(apiError(err)))
      .finally(() => setLoading(false))
  }

  useEffect(() => { fetch() }, [tenant.id])

  const openAsOwner = () => {
    if (!data) return
    try {
      // safeB64Encode handles Cyrillic names via encodeURIComponent
      const userEncoded = safeB64Encode(JSON.stringify(data.user))
      const url = `/admin-access?token=${encodeURIComponent(data.tempToken)}&user=${encodeURIComponent(userEncoded)}`
      window.open(url, '_blank')
      setOpened(true)
    } catch (e) {
      setError(`Ошибка кодирования: ${String(e)}`)
    }
  }

  return (
    <Modal title="Войти как владелец" onClose={onClose}>
      <p className="text-sm text-gray-500">Тенант: <span className="font-medium text-gray-800">{tenant.name}</span></p>

      {data?.wasDisabled && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 text-xs text-orange-800">
          Тенант был заблокирован — временно разблокирован для входа. Заблокируй обратно после проверки.
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-800 space-y-1">
        <p className="font-medium">Как работает вход:</p>
        <p>• Пароль владельца <strong>не меняется</strong></p>
        <p>• Открывается новая вкладка — сразу кабинет владельца</p>
        <p>• Токен действителен 15 минут</p>
      </div>

      {loading && <p className="text-sm text-gray-500 text-center py-3">Генерация токена...</p>}

      {error && (
        <div className="space-y-2">
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">{error}</div>
          <button onClick={fetch}
            className="w-full text-sm border border-gray-300 py-2 rounded-xl hover:bg-gray-50">
            Повторить
          </button>
        </div>
      )}

      {data && (
        <div className="bg-gray-50 rounded-xl p-3 space-y-1">
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Владелец</span>
            <span className="font-medium text-gray-900">{data.user.name}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Телефон</span>
            <span className="font-mono text-gray-700">{data.user.phone}</span>
          </div>
        </div>
      )}

      {data && (
        <div className="flex gap-2">
          <button onClick={openAsOwner}
            className={`flex-1 py-3 rounded-xl text-sm font-semibold transition-colors ${
              opened ? 'bg-green-600 text-white' : 'bg-gray-900 hover:bg-gray-800 text-white'
            }`}>
            {opened ? '✓ Вкладка открыта' : 'Войти как владелец →'}
          </button>
          <button onClick={onClose} className="border border-gray-300 px-4 py-3 rounded-xl text-sm">Закрыть</button>
        </div>
      )}
    </Modal>
  )
}

function DeleteTenantModal({ tenant, onClose, onDeleted }: { tenant: Tenant; onClose: () => void; onDeleted: () => void }) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirm, setConfirm] = useState('')

  const handleDelete = async () => {
    setLoading(true); setError(null)
    try {
      await adminApi().delete(`/admin/tenants/${tenant.id}`)
      onDeleted()
    } catch (err) { setError(apiError(err)); setLoading(false) }
  }

  return (
    <Modal title="Удалить тенанта?" onClose={onClose}>
      <div className="text-center">
        <div className="text-3xl mb-1">⚠️</div>
        <p className="text-sm text-gray-500">
          Удалятся все данные: велосипеды, аренды, смены, пользователи.
          <br /><span className="font-semibold text-red-600">Необратимо.</span>
        </p>
      </div>
      <div className="bg-gray-50 rounded-xl px-3 py-2 text-sm">
        <span className="font-medium">{tenant.name}</span>
        <br /><span className="text-gray-400 text-xs font-mono">{tenant.id}</span>
      </div>
      {error && <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">{error}</div>}
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">
          Введите <span className="font-bold text-gray-800">УДАЛИТЬ</span>
        </label>
        <input value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="УДАЛИТЬ"
          className="w-full rounded-xl border-gray-300 text-sm" />
      </div>
      <div className="flex gap-2">
        <button onClick={handleDelete} disabled={loading || confirm !== 'УДАЛИТЬ'}
          className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white py-3 rounded-xl text-sm font-semibold">
          {loading ? 'Удаление...' : 'Удалить'}
        </button>
        <button onClick={onClose} className="flex-1 border border-gray-300 py-3 rounded-xl text-sm">Отмена</button>
      </div>
    </Modal>
  )
}

function AddManagerModal({ tenant, onClose, onAdded }: { tenant: Tenant; onClose: () => void; onAdded: () => void }) {
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const nameRef = useRef<HTMLInputElement>(null)
  const phoneRef = useRef<HTMLInputElement>(null)
  const passwordRef = useRef<HTMLInputElement>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setError(null)
    const name = nameRef.current?.value?.trim() ?? ''
    const phone = phoneRef.current?.value?.trim() ?? ''
    const password = passwordRef.current?.value ?? ''
    if (!name) { setError('Введите имя'); return }
    if (!phone) { setError('Введите телефон'); return }
    if (password.length < 6) { setError('Пароль — минимум 6 символов'); return }
    setLoading(true)
    try {
      await adminApi().post(`/admin/tenants/${tenant.id}/users`, { name, phone, password })
      onAdded(); onClose()
    } catch (err) { setError(apiError(err)) } finally { setLoading(false) }
  }

  return (
    <Modal title={`Добавить менеджера — ${tenant.name}`} onClose={onClose}>
      {error && <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">{error}</div>}
      <form onSubmit={handleSubmit} className="space-y-3" autoComplete="off">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Имя</label>
          <input ref={nameRef} placeholder="Имя менеджера" className="w-full rounded-xl border-gray-300 text-sm" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Телефон</label>
          <input ref={phoneRef} type="tel" placeholder="+79001234567" className="w-full rounded-xl border-gray-300 text-sm" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Пароль</label>
          <input ref={passwordRef} type="password" placeholder="Минимум 6 символов" className="w-full rounded-xl border-gray-300 text-sm" autoComplete="new-password" />
        </div>
        <div className="flex gap-2 pt-1">
          <button type="submit" disabled={loading}
            className="flex-1 bg-gray-900 text-white py-3 rounded-xl text-sm font-semibold disabled:opacity-60">
            {loading ? 'Добавление...' : 'Добавить'}
          </button>
          <button type="button" onClick={onClose} className="flex-1 border border-gray-300 py-3 rounded-xl text-sm">Отмена</button>
        </div>
      </form>
    </Modal>
  )
}

// ─── Users panel (expandable) ─────────────────────────────────────────────────

function UsersList({ tenantId, tenantName, onAddManager }: { tenantId: string; tenantName: string; onAddManager: () => void }) {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [toggling, setToggling] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<string | null>(null)

  const load = () => {
    setLoading(true)
    adminApi().get(`/admin/tenants/${tenantId}/users`)
      .then(r => setUsers(r.data.users))
      .catch(err => setError(apiError(err)))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [tenantId])

  const toggleUser = async (userId: string) => {
    setToggling(userId)
    try {
      await adminApi().patch(`/admin/users/${userId}/toggle`)
      load()
    } catch (err) { alert(apiError(err)) } finally { setToggling(null) }
  }

  const deleteUser = async (user: User) => {
    if (!confirm(`Удалить ${user.name} (${user.phone})?`)) return
    setDeleting(user.id)
    try {
      await adminApi().delete(`/admin/users/${user.id}`)
      load()
    } catch (err) { alert(apiError(err)) } finally { setDeleting(null) }
  }

  if (loading) return <div className="py-3 text-center text-xs text-gray-400">Загрузка...</div>
  if (error) return <div className="py-2 text-xs text-red-500">{error}</div>

  return (
    <div className="mt-3 border-t border-gray-100 pt-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-gray-500">Пользователи ({users.length})</span>
        <button onClick={onAddManager}
          className="text-xs bg-gray-900 text-white px-2 py-1 rounded-lg hover:bg-gray-800">
          + Менеджер
        </button>
      </div>
      <div className="space-y-1.5">
        {users.map(u => (
          <div key={u.id} className="flex items-center justify-between bg-gray-50 rounded-xl px-3 py-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-sm font-medium text-gray-800 truncate">{u.name}</span>
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${u.role === 'owner' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                  {u.role === 'owner' ? 'владелец' : 'менеджер'}
                </span>
                {!u.isActive && <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full">откл</span>}
              </div>
              <p className="text-xs text-gray-400">{u.phone}</p>
              {u.lastLoginAt && (
                <p className="text-xs text-gray-400">был {fmtDate(u.lastLoginAt)}</p>
              )}
            </div>
            {u.role !== 'owner' && (
              <div className="flex items-center gap-1 ml-2 shrink-0">
                <button
                  onClick={() => toggleUser(u.id)}
                  disabled={toggling === u.id}
                  className="text-xs border border-gray-300 px-2 py-1 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                >
                  {u.isActive ? 'Откл' : 'Вкл'}
                </button>
                <button
                  onClick={() => deleteUser(u)}
                  disabled={deleting === u.id}
                  className="text-xs border border-red-200 text-red-600 px-2 py-1 rounded-lg hover:bg-red-50 disabled:opacity-50"
                >
                  {deleting === u.id ? '...' : 'Удалить'}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Edit limits modal ────────────────────────────────────────────────────────

function EditLimitsModal({ tenant, onClose, onSaved }: { tenant: Tenant; onClose: () => void; onSaved: () => void }) {
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [value, setValue] = useState(() => {
    try { return JSON.stringify(tenant.limits ?? {}, null, 2) } catch { return '{}' }
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setError(null)
    let parsed: Record<string, unknown>
    try { parsed = JSON.parse(value) } catch {
      setError('Невалидный JSON. Проверьте синтаксис.'); return
    }
    if (typeof parsed !== 'object' || Array.isArray(parsed)) {
      setError('Лимиты должны быть объектом, например {"maxBikes": 20}'); return
    }
    setLoading(true)
    try {
      await adminApi().patch(`/admin/tenants/${tenant.id}/limits`, { limits: parsed })
      setSuccess(true); onSaved(); setTimeout(onClose, 700)
    } catch (err) { setError(apiError(err)) } finally { setLoading(false) }
  }

  return (
    <Modal title={`Лимиты — ${tenant.name}`} onClose={onClose}>
      <p className="text-xs text-gray-500">
        JSON-объект с ограничениями тенанта. Пример: <code className="bg-gray-100 px-1 rounded">{"{"}"maxBikes": 20, "maxPoints": 3{"}"}</code>
      </p>
      {error && <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">{error}</div>}
      {success && <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-sm text-green-700">Сохранено</div>}
      <form onSubmit={handleSubmit} className="space-y-3">
        <textarea
          value={value}
          onChange={e => setValue(e.target.value)}
          rows={8}
          spellCheck={false}
          className="w-full rounded-xl border-gray-300 text-xs font-mono resize-none"
        />
        <div className="flex gap-2">
          <button type="submit" disabled={loading || success}
            className="flex-1 bg-gray-900 text-white py-3 rounded-xl text-sm font-semibold disabled:opacity-60">
            {loading ? 'Сохранение...' : 'Сохранить'}
          </button>
          <button type="button" onClick={onClose} className="flex-1 border border-gray-300 py-3 rounded-xl text-sm">Отмена</button>
        </div>
      </form>
    </Modal>
  )
}

// ─── Tenant card ──────────────────────────────────────────────────────────────

type TenantAction = 'edit' | 'temp-access' | 'delete' | 'add-manager' | 'limits'

function TenantCard({
  tenant, onAction, onToggled,
}: {
  tenant: Tenant
  onAction: (action: TenantAction, t: Tenant) => void
  onToggled: () => void
}) {
  const owner = tenant.users[0]
  const [expanded, setExpanded] = useState(false)
  const [showDetails, setShowDetails] = useState(false)
  const [toggling, setToggling] = useState(false)
  const [addManager, setAddManager] = useState(false)
  const [usersKey, setUsersKey] = useState(0)

  const toggleTenant = async () => {
    setToggling(true)
    try {
      await adminApi().patch(`/admin/tenants/${tenant.id}/toggle`)
      onToggled()
    } catch (err) { alert(apiError(err)) } finally { setToggling(false) }
  }

  const borderColor = tenant.status === 'blocked' ? 'border-red-200 bg-red-50/30'
    : tenant.status === 'paused' ? 'border-yellow-200 bg-yellow-50/20'
    : 'border-gray-200'

  return (
    <div className={`bg-white border rounded-2xl p-4 ${borderColor}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <p className="font-semibold text-gray-900 truncate">{tenant.name}</p>
            <StatusBadge status={tenant.status} />
            {tenant.isDemoTenant && (() => {
              const days = daysLeft(tenant.expiresAt)
              const color = days === null ? 'bg-yellow-100 text-yellow-700'
                : days <= 0 ? 'bg-red-100 text-red-700'
                : days <= 1 ? 'bg-orange-100 text-orange-700'
                : 'bg-yellow-100 text-yellow-700'
              const label = days === null ? 'demo'
                : days <= 0 ? 'demo · истёк'
                : `demo · ${days} дн`
              return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${color}`}>{label}</span>
            })()}
          </div>
          <p className="text-xs text-gray-400 mt-0.5 font-mono truncate">{tenant.id}</p>
        </div>
        <p className="text-xs text-gray-400 whitespace-nowrap shrink-0">{fmtDate(tenant.createdAt)}</p>
      </div>

      {owner && (
        <div className="mt-3 bg-gray-50 rounded-xl px-3 py-2 flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-800">{owner.name}</p>
            <p className="text-xs text-gray-500">{owner.phone}</p>
          </div>
          <span className={`text-xs px-2 py-0.5 rounded-full ${owner.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'}`}>
            {owner.isActive ? 'активен' : 'отключён'}
          </span>
        </div>
      )}

      <div className="mt-3 flex gap-3 text-xs text-gray-400">
        <span>👥 {tenant._count.users}</span>
        <span>🚲 {tenant._count.bikes}</span>
        <span>📋 {tenant._count.rentals}</span>
        {tenant._count.incidents > 0 && (
          <span className="text-red-500">⚠️ {tenant._count.incidents}</span>
        )}
      </div>

      {/* Action buttons */}
      <div className="mt-3 grid grid-cols-2 gap-1.5">
        <button onClick={() => onAction('edit', tenant)}
          className="text-xs border border-gray-300 py-2 rounded-xl hover:bg-gray-50 font-medium">
          Редактировать
        </button>
        <button onClick={() => onAction('temp-access', tenant)}
          className="text-xs border border-gray-300 py-2 rounded-xl hover:bg-gray-50 font-medium">
          Войти как владелец
        </button>
        <button
          onClick={toggleTenant} disabled={toggling}
          className={`text-xs border py-2 rounded-xl font-medium disabled:opacity-50 ${
            tenant.isDisabled
              ? 'border-green-300 text-green-700 hover:bg-green-50'
              : 'border-orange-300 text-orange-700 hover:bg-orange-50'
          }`}>
          {toggling ? '...' : tenant.isDisabled ? 'Разблокировать' : 'Заблокировать'}
        </button>
        <button onClick={() => onAction('limits', tenant)}
          className="text-xs border border-gray-300 py-2 rounded-xl hover:bg-gray-50 font-medium">
          Лимиты
        </button>
        <button onClick={() => onAction('delete', tenant)}
          className="col-span-2 text-xs border border-red-200 text-red-600 py-2 rounded-xl hover:bg-red-50 font-medium">
          Удалить
        </button>
      </div>

      {/* Details panel toggle */}
      <button
        onClick={() => setShowDetails(!showDetails)}
        className="mt-2 w-full text-xs text-blue-500 hover:text-blue-700 py-1 flex items-center justify-center gap-1">
        {showDetails ? '▲ Скрыть детали' : '▼ Детали, usage, инциденты'}
      </button>

      {showDetails && (
        <TenantDetailsPanel tenantId={tenant.id} onStatusChanged={onToggled} />
      )}

      {/* Expandable users */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="mt-1 w-full text-xs text-gray-400 hover:text-gray-600 py-1 flex items-center justify-center gap-1">
        {expanded ? '▲ Скрыть пользователей' : '▼ Показать пользователей'}
      </button>

      {expanded && (
        <UsersList
          key={usersKey}
          tenantId={tenant.id}
          tenantName={tenant.name}
          onAddManager={() => setAddManager(true)}
        />
      )}

      {addManager && (
        <AddManagerModal
          tenant={tenant}
          onClose={() => setAddManager(false)}
          onAdded={() => { setUsersKey(k => k + 1); setAddManager(false) }}
        />
      )}
    </div>
  )
}

// ─── Tenant details panel ──────────────────────────────────────────────────────

function HealthBar({ score }: { score: number }) {
  const color = score >= 80 ? 'bg-green-500' : score >= 50 ? 'bg-yellow-500' : 'bg-red-500'
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-gray-100 rounded-full h-1.5">
        <div className={`${color} h-1.5 rounded-full transition-all`} style={{ width: `${score}%` }} />
      </div>
      <span className="text-xs font-medium text-gray-700 w-8 text-right">{score}</span>
    </div>
  )
}

function TenantDetailsPanel({ tenantId, onStatusChanged }: { tenantId: string; onStatusChanged: () => void }) {
  const [data, setData] = useState<TenantDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [changingStatus, setChangingStatus] = useState(false)
  const [newStatus, setNewStatus] = useState<TenantStatus>('active')
  const [reason, setReason] = useState('')
  const [showStatusForm, setShowStatusForm] = useState(false)

  useEffect(() => {
    setLoading(true); setError(null)
    adminApi().get(`/admin/tenants/${tenantId}/details`)
      .then(r => {
        setData(r.data)
        setNewStatus(r.data.tenant.status)
      })
      .catch(err => setError(apiError(err)))
      .finally(() => setLoading(false))
  }, [tenantId])

  const handleStatusChange = async () => {
    setChangingStatus(true)
    try {
      await adminApi().patch(`/admin/tenants/${tenantId}/status`, { status: newStatus, reason: reason || undefined })
      onStatusChanged()
      const r = await adminApi().get(`/admin/tenants/${tenantId}/details`)
      setData(r.data)
      setShowStatusForm(false)
      setReason('')
    } catch (err) { alert(apiError(err)) } finally { setChangingStatus(false) }
  }

  if (loading) return <div className="py-4 text-center text-xs text-gray-400">Загрузка деталей...</div>
  if (error) return <div className="py-4 text-xs text-red-600">{error}</div>
  if (!data) return null

  const { tenant, healthScore, usage, recentAuditLogs, openIncidents } = data

  return (
    <div className="mt-3 border-t border-gray-100 pt-3 space-y-3">
      {/* Health + Usage overview */}
      <div className="bg-gray-50 rounded-xl p-3 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-gray-600">Health Score</span>
          <span className="text-xs text-gray-400">{tenant.timezone}</span>
        </div>
        <HealthBar score={healthScore} />
        <div className="grid grid-cols-4 gap-2 mt-2">
          {[
            { label: 'Аренды 7д', val: fmtNum(usage.last7d.rentals) },
            { label: 'Смены 7д', val: fmtNum(usage.last7d.shifts) },
            { label: 'Ошибки 7д', val: fmtNum(usage.last7d.errors) },
            { label: 'Выручка 7д', val: `${fmtNum(usage.last7d.revenue)}₽` },
          ].map(({ label, val }) => (
            <div key={label} className="text-center">
              <p className="text-sm font-semibold text-gray-900">{val}</p>
              <p className="text-[10px] text-gray-400">{label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Open incidents */}
      {openIncidents.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3">
          <p className="text-xs font-medium text-red-700 mb-2">Открытые инциденты ({openIncidents.length})</p>
          <div className="space-y-1.5">
            {openIncidents.map(inc => (
              <div key={inc.id} className="flex items-center justify-between">
                <p className="text-xs text-red-800 truncate">{inc.title}</p>
                <SeverityBadge severity={inc.severity} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Status change */}
      <div>
        <button onClick={() => setShowStatusForm(!showStatusForm)}
          className="text-xs text-blue-600 hover:underline">
          {showStatusForm ? 'Отмена' : 'Изменить статус'}
        </button>
        {showStatusForm && (
          <div className="mt-2 space-y-2 bg-blue-50 border border-blue-200 rounded-xl p-3">
            <select value={newStatus} onChange={e => setNewStatus(e.target.value as TenantStatus)}
              className="w-full text-xs border border-blue-300 rounded-lg px-2 py-1.5 bg-white">
              <option value="active">активен</option>
              <option value="trial">пробный</option>
              <option value="paused">на паузе</option>
              <option value="blocked">заблокирован</option>
            </select>
            <input value={reason} onChange={e => setReason(e.target.value)}
              placeholder="Причина (опционально)"
              className="w-full text-xs border border-blue-300 rounded-lg px-2 py-1.5 bg-white" />
            <button onClick={handleStatusChange} disabled={changingStatus}
              className="w-full text-xs bg-blue-600 hover:bg-blue-700 text-white py-1.5 rounded-lg font-medium disabled:opacity-50">
              {changingStatus ? 'Сохранение...' : 'Применить'}
            </button>
          </div>
        )}
      </div>

      {/* Recent audit */}
      {recentAuditLogs.length > 0 && (
        <div>
          <p className="text-xs font-medium text-gray-600 mb-1.5">Последние события</p>
          <div className="space-y-1">
            {recentAuditLogs.slice(0, 5).map(log => (
              <div key={log.id} className="flex items-start gap-2 text-xs text-gray-500">
                <span className="text-gray-300 shrink-0">{fmtDate(log.createdAt)}</span>
                <span className="font-mono text-gray-600 truncate">{log.action}</span>
                {log.entityType && <span className="text-gray-400 shrink-0">{log.entityType}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Platform Dashboard tab ────────────────────────────────────────────────────

function DashboardTab() {
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = () => {
    setLoading(true); setError(null)
    adminApi().get('/admin/dashboard')
      .then(r => setStats(r.data))
      .catch(err => setError(apiError(err)))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [])

  if (loading) return <div className="text-center py-12 text-gray-400 text-sm">Загрузка...</div>
  if (error) return <div className="p-4 text-sm text-red-600">{error}</div>
  if (!stats) return null

  const kpis = [
    { label: 'Активные', val: stats.status.active, cls: 'text-green-600 bg-green-50', border: 'border-green-200' },
    { label: 'Пробные', val: stats.status.trial, cls: 'text-blue-600 bg-blue-50', border: 'border-blue-200' },
    { label: 'На паузе', val: stats.status.paused, cls: 'text-yellow-600 bg-yellow-50', border: 'border-yellow-200' },
    { label: 'Заблокированы', val: stats.status.blocked, cls: 'text-red-600 bg-red-50', border: 'border-red-200' },
  ]

  return (
    <div className="p-4 max-w-2xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-gray-700">Обзор платформы</h2>
        <button onClick={load} className="text-xs border border-gray-300 px-3 py-1.5 rounded-lg hover:bg-gray-50">
          Обновить
        </button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 gap-2">
        {kpis.map(({ label, val, cls, border }) => (
          <div key={label} className={`${cls} ${border} border rounded-2xl p-3`}>
            <p className="text-2xl font-bold">{val}</p>
            <p className="text-xs font-medium mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* Incidents alert */}
      {stats.openIncidents > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-3 flex items-center gap-3">
          <span className="text-2xl">⚠️</span>
          <div>
            <p className="text-sm font-semibold text-red-800">Открытые инциденты: {stats.openIncidents}</p>
            <p className="text-xs text-red-600">Перейдите во вкладку «Инциденты» для разбора</p>
          </div>
        </div>
      )}

      {/* Noisy tenants */}
      {stats.noisyTenants.length > 0 && (
        <div>
          <p className="text-sm font-semibold text-gray-700 mb-2">Шумные тенанты (7 дней)</p>
          <div className="space-y-2">
            {stats.noisyTenants.map((nt) => (
              <div key={nt.tenantId} className="bg-white border border-gray-200 rounded-xl p-3">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{nt.tenant?.name ?? nt.tenantId}</p>
                    <p className="text-xs text-gray-400 font-mono">{nt.tenantId.slice(0, 8)}...</p>
                  </div>
                  {nt.tenant && <StatusBadge status={nt.tenant.status} />}
                </div>
                <div className="grid grid-cols-3 gap-2 text-center text-xs">
                  <div>
                    <p className="font-semibold text-gray-900">{fmtNum(nt.rentals)}</p>
                    <p className="text-gray-400">аренды</p>
                  </div>
                  <div className={nt.errors > 10 ? 'text-red-600' : ''}>
                    <p className="font-semibold">{fmtNum(nt.errors)}</p>
                    <p className={nt.errors > 10 ? 'text-red-400' : 'text-gray-400'}>ошибки</p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{fmtNum(nt.revenue)}₽</p>
                    <p className="text-gray-400">выручка</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {stats.noisyTenants.length === 0 && stats.openIncidents === 0 && (
        <div className="text-center py-8 text-gray-400 text-sm">Всё спокойно. Нет шумных тенантов и инцидентов.</div>
      )}
    </div>
  )
}

// ─── Incidents tab ────────────────────────────────────────────────────────────

function IncidentsTab() {
  const [incidents, setIncidents] = useState<Incident[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [severityFilter, setSeverityFilter] = useState<string>('')
  const [showCreate, setShowCreate] = useState(false)
  const [newTitle, setNewTitle] = useState('')
  const [newTenantId, setNewTenantId] = useState('')
  const [newSeverity, setNewSeverity] = useState<IncidentSeverity>('medium')
  const [newCategory, setNewCategory] = useState<IncidentCategory>('other')
  const [creating, setCreating] = useState(false)

  const load = () => {
    setLoading(true); setError(null)
    const params: Record<string, string> = {}
    if (statusFilter) params.status = statusFilter
    if (severityFilter) params.severity = severityFilter
    adminApi().get('/admin/incidents', { params })
      .then(r => { setIncidents(r.data.incidents); setTotal(r.data.total) })
      .catch(err => setError(apiError(err)))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [statusFilter, severityFilter])

  const updateStatus = async (id: string, status: IncidentStatus) => {
    try {
      await adminApi().patch(`/admin/incidents/${id}`, { status })
      load()
    } catch (err) { alert(apiError(err)) }
  }

  const createIncident = async () => {
    if (!newTitle.trim() || !newTenantId.trim()) { alert('Укажите название и tenant_id'); return }
    setCreating(true)
    try {
      await adminApi().post('/admin/incidents', {
        tenantId: newTenantId, title: newTitle, severity: newSeverity, category: newCategory,
      })
      setShowCreate(false); setNewTitle(''); setNewTenantId('')
      load()
    } catch (err) { alert(apiError(err)) } finally { setCreating(false) }
  }

  const statusLabel: Record<IncidentStatus, string> = {
    open: 'открыт', investigating: 'изучается', resolved: 'решён', closed: 'закрыт',
  }
  const nextStatus: Record<IncidentStatus, IncidentStatus> = {
    open: 'investigating', investigating: 'resolved', resolved: 'closed', closed: 'open',
  }

  return (
    <div className="p-4 max-w-2xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-gray-700">Инциденты {!loading && `(${total})`}</h2>
        <div className="flex gap-2">
          <button onClick={load} className="text-xs border border-gray-300 px-3 py-1.5 rounded-lg hover:bg-gray-50">
            Обновить
          </button>
          <button onClick={() => setShowCreate(!showCreate)}
            className="text-xs bg-gray-900 text-white px-3 py-1.5 rounded-lg hover:bg-gray-800">
            + Создать
          </button>
        </div>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 space-y-2">
          <p className="text-sm font-medium text-blue-800">Новый инцидент</p>
          <input value={newTitle} onChange={e => setNewTitle(e.target.value)}
            placeholder="Название инцидента *"
            className="w-full text-sm border border-blue-300 rounded-xl px-3 py-2 bg-white" />
          <input value={newTenantId} onChange={e => setNewTenantId(e.target.value)}
            placeholder="tenant_id *"
            className="w-full text-sm border border-blue-300 rounded-xl px-3 py-2 bg-white font-mono" />
          <div className="grid grid-cols-2 gap-2">
            <select value={newSeverity} onChange={e => setNewSeverity(e.target.value as IncidentSeverity)}
              className="text-sm border border-blue-300 rounded-xl px-2 py-2 bg-white">
              <option value="low">низкий</option>
              <option value="medium">средний</option>
              <option value="high">высокий</option>
              <option value="critical">критичный</option>
            </select>
            <select value={newCategory} onChange={e => setNewCategory(e.target.value as IncidentCategory)}
              className="text-sm border border-blue-300 rounded-xl px-2 py-2 bg-white">
              <option value="integration">интеграция</option>
              <option value="performance">производительность</option>
              <option value="data">данные</option>
              <option value="billing">биллинг</option>
              <option value="security">безопасность</option>
              <option value="other">другое</option>
            </select>
          </div>
          <div className="flex gap-2">
            <button onClick={createIncident} disabled={creating}
              className="flex-1 text-sm bg-blue-600 text-white py-2 rounded-xl font-medium disabled:opacity-50">
              {creating ? 'Создание...' : 'Создать'}
            </button>
            <button onClick={() => setShowCreate(false)}
              className="flex-1 text-sm border border-blue-300 py-2 rounded-xl text-blue-700">
              Отмена
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2">
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="text-xs border border-gray-300 rounded-lg px-2 py-1.5 bg-white">
          <option value="">Все статусы</option>
          <option value="open">открытые</option>
          <option value="investigating">изучаются</option>
          <option value="resolved">решённые</option>
          <option value="closed">закрытые</option>
        </select>
        <select value={severityFilter} onChange={e => setSeverityFilter(e.target.value)}
          className="text-xs border border-gray-300 rounded-lg px-2 py-1.5 bg-white">
          <option value="">Любая важность</option>
          <option value="critical">критичный</option>
          <option value="high">высокий</option>
          <option value="medium">средний</option>
          <option value="low">низкий</option>
        </select>
      </div>

      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl p-3">{error}</div>}
      {loading ? (
        <div className="text-center py-8 text-gray-400 text-sm">Загрузка...</div>
      ) : incidents.length === 0 ? (
        <div className="text-center py-8 text-gray-400 text-sm">Инцидентов нет</div>
      ) : (
        <div className="space-y-2">
          {incidents.map(inc => (
            <div key={inc.id} className={`bg-white border rounded-2xl p-4 ${
              inc.status === 'open' || inc.status === 'investigating' ? 'border-orange-200' : 'border-gray-200'
            }`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{inc.title}</p>
                  {inc.tenant && (
                    <p className="text-xs text-gray-500 mt-0.5">{inc.tenant.name}</p>
                  )}
                </div>
                <SeverityBadge severity={inc.severity} />
              </div>
              <div className="mt-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    inc.status === 'open' ? 'bg-red-100 text-red-700'
                    : inc.status === 'investigating' ? 'bg-orange-100 text-orange-700'
                    : 'bg-gray-100 text-gray-600'
                  }`}>
                    {statusLabel[inc.status]}
                  </span>
                  <span className="text-xs text-gray-400">{fmtDate(inc.createdAt)}</span>
                </div>
                {inc.status !== 'closed' && (
                  <button onClick={() => updateStatus(inc.id, nextStatus[inc.status])}
                    className="text-xs border border-gray-300 px-2 py-1 rounded-lg hover:bg-gray-50">
                    → {statusLabel[nextStatus[inc.status]]}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Audit log tab ────────────────────────────────────────────────────────────

function AuditLogTab() {
  const [logs, setLogs] = useState<AdminAuditEntry[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [offset, setOffset] = useState(0)
  const LIMIT = 50

  const load = (off = 0) => {
    setLoading(true); setError(null)
    adminApi().get('/admin/audit', { params: { limit: LIMIT, offset: off } })
      .then(r => { setLogs(r.data.logs); setTotal(r.data.total); setOffset(off) })
      .catch(err => setError(apiError(err)))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [])

  return (
    <div className="p-4 max-w-2xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-gray-700">Журнал действий администратора {!loading && `(${total})`}</h2>
        <button onClick={() => load()} className="text-xs border border-gray-300 px-3 py-1.5 rounded-lg hover:bg-gray-50">
          Обновить
        </button>
      </div>

      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl p-3">{error}</div>}
      {loading ? (
        <div className="text-center py-8 text-gray-400 text-sm">Загрузка...</div>
      ) : logs.length === 0 ? (
        <div className="text-center py-8 text-gray-400 text-sm">Действий пока нет</div>
      ) : (
        <>
          <div className="space-y-1.5">
            {logs.map(log => (
              <div key={log.id} className="bg-white border border-gray-200 rounded-xl p-3">
                <div className="flex items-start gap-2">
                  <span className="text-xs text-gray-400 whitespace-nowrap shrink-0 mt-0.5">
                    {fmtDate(log.createdAt)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-mono font-medium text-gray-900">{log.action}</span>
                      {log.targetType && (
                        <span className="text-xs text-gray-500">{log.targetType}</span>
                      )}
                      {log.adminKey && (
                        <span className="text-xs text-gray-400 font-mono">…{log.adminKey}</span>
                      )}
                    </div>
                    {log.targetId && (
                      <p className="text-xs text-gray-400 font-mono truncate mt-0.5">{log.targetId}</p>
                    )}
                    {log.reason && (
                      <p className="text-xs text-gray-600 mt-0.5 italic">{log.reason}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between text-xs text-gray-400">
            <button disabled={offset === 0} onClick={() => load(Math.max(0, offset - LIMIT))}
              className="border border-gray-300 px-3 py-1.5 rounded-lg hover:bg-gray-50 disabled:opacity-40">
              ← Назад
            </button>
            <span>{offset + 1}–{Math.min(offset + LIMIT, total)} из {total}</span>
            <button disabled={offset + LIMIT >= total} onClick={() => load(offset + LIMIT)}
              className="border border-gray-300 px-3 py-1.5 rounded-lg hover:bg-gray-50 disabled:opacity-40">
              Вперёд →
            </button>
          </div>
        </>
      )}
    </div>
  )
}

// ─── Operations Tab ───────────────────────────────────────────────────────────

interface RentalRow {
  id: string; status: string; pricingMode: string
  actualStartAt: string; plannedReturnAt: string; actualReturnAt: string | null
  basePrice: number; totalPrice: number | null; managerNotes: string | null
  bike: { id: string; code: string; status: string } | null
  customer: { id: string; phone: string }
  manager: { id: string; name: string }
  pickupPoint: { id: string; name: string }
}

interface BikeRow {
  id: string; code: string; model: string | null; status: string; notes: string | null
  point: { id: string; name: string } | null
  tariff: { id: string; name: string } | null
  rentals: Array<{ id: string; actualStartAt: string }>
}

function fmtDateTime(d: string) {
  return new Date(d).toLocaleString('ru', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

function hoursAgo(d: string) {
  return Math.floor((Date.now() - new Date(d).getTime()) / (1000 * 60 * 60))
}

function TenantSelector({ tenants, value, onChange }: {
  tenants: Tenant[]; value: string; onChange: (id: string) => void
}) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
      className="w-full text-sm border border-gray-300 rounded-xl px-3 py-2 bg-white">
      <option value="">— выбери тенанта —</option>
      {tenants.map(t => (
        <option key={t.id} value={t.id}>{t.name} ({t.users[0]?.phone ?? '?'})</option>
      ))}
    </select>
  )
}

function ForceCloseModal({ rental, onClose, onDone }: {
  rental: RentalRow; onClose: () => void; onDone: () => void
}) {
  const [reason, setReason] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handle = async () => {
    setLoading(true); setError(null)
    try {
      await adminApi().post(`/admin/rentals/${rental.id}/force-close`, { reason: reason || undefined })
      onDone(); onClose()
    } catch (err) { setError(apiError(err)) } finally { setLoading(false) }
  }

  return (
    <Modal title="Принудительно закрыть аренду" onClose={onClose}>
      <div className="space-y-1 text-sm bg-gray-50 rounded-xl p-3">
        <div className="flex justify-between"><span className="text-gray-500">Велосипед</span><span className="font-medium">{rental.bike?.code ?? '—'}</span></div>
        <div className="flex justify-between"><span className="text-gray-500">Клиент</span><span className="font-medium">{rental.customer?.phone ?? '—'}</span></div>
        <div className="flex justify-between"><span className="text-gray-500">Начало</span><span className="font-medium">{fmtDateTime(rental.actualStartAt)}</span></div>
        <div className="flex justify-between"><span className="text-gray-500">Длительность</span><span className="font-medium">{hoursAgo(rental.actualStartAt)}ч</span></div>
      </div>
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">
        Аренда закроется по базовой цене ({rental.basePrice}₽), велосипед станет «свободен». Действие записывается в аудит.
      </div>
      {error && <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">{error}</div>}
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Причина (необязательно)</label>
        <input value={reason} onChange={e => setReason(e.target.value)}
          placeholder="Например: велосипед нашли, клиент уехал"
          className="w-full text-sm border border-gray-300 rounded-xl px-3 py-2" />
      </div>
      <div className="flex gap-2">
        <button onClick={handle} disabled={loading}
          className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white py-3 rounded-xl text-sm font-semibold">
          {loading ? 'Закрываю...' : 'Закрыть аренду'}
        </button>
        <button onClick={onClose} className="flex-1 border border-gray-300 py-3 rounded-xl text-sm">Отмена</button>
      </div>
    </Modal>
  )
}

function OperationsTab({ tenants }: { tenants: Tenant[] }) {
  const [tenantId, setTenantId] = useState('')
  const [rentals, setRentals] = useState<RentalRow[]>([])
  const [bikes, setBikes] = useState<BikeRow[]>([])
  const [rentalsLoading, setRentalsLoading] = useState(false)
  const [bikesLoading, setBikesLoading] = useState(false)
  const [forceClose, setForceClose] = useState<RentalRow | null>(null)
  const [bikeStatusChanging, setBikeStatusChanging] = useState<string | null>(null)
  const [bikeStatusReason, setBikeStatusReason] = useState<Record<string, string>>({})

  const loadRentals = (tid: string) => {
    if (!tid) return
    setRentalsLoading(true)
    adminApi().get(`/admin/tenants/${tid}/rentals`, { params: { status: 'active' } })
      .then(r => setRentals(r.data.rentals))
      .catch(() => setRentals([]))
      .finally(() => setRentalsLoading(false))
  }

  const loadBikes = (tid: string) => {
    if (!tid) return
    setBikesLoading(true)
    adminApi().get(`/admin/tenants/${tid}/bikes`)
      .then(r => setBikes(r.data.bikes))
      .catch(() => setBikes([]))
      .finally(() => setBikesLoading(false))
  }

  const handleTenantChange = (tid: string) => {
    setTenantId(tid)
    setRentals([]); setBikes([])
    if (tid) { loadRentals(tid); loadBikes(tid) }
  }

  const changeBikeStatus = async (bikeId: string, status: string) => {
    setBikeStatusChanging(bikeId)
    try {
      await adminApi().patch(`/admin/bikes/${bikeId}/status`, {
        status,
        reason: bikeStatusReason[bikeId] || undefined,
      })
      loadBikes(tenantId)
    } catch (err) { alert(apiError(err)) } finally { setBikeStatusChanging(null) }
  }

  const stuckBikes = bikes.filter(b => b.status === 'in_use')
  const otherBikes = bikes.filter(b => b.status !== 'in_use')

  return (
    <div className="p-4 max-w-2xl mx-auto space-y-4">
      <div>
        <h2 className="font-semibold text-gray-700 mb-2">Операции — выбери тенанта</h2>
        <TenantSelector tenants={tenants} value={tenantId} onChange={handleTenantChange} />
      </div>

      {tenantId && (
        <>
          {/* Active rentals */}
          <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <div>
                <h3 className="font-semibold text-gray-800">Активные аренды</h3>
                <p className="text-xs text-gray-400 mt-0.5">Здесь можно принудительно закрыть застрявшую аренду</p>
              </div>
              <button onClick={() => loadRentals(tenantId)}
                className="text-xs border border-gray-300 px-2 py-1 rounded-lg hover:bg-gray-50">
                Обновить
              </button>
            </div>
            {rentalsLoading ? (
              <div className="py-6 text-center text-xs text-gray-400">Загрузка...</div>
            ) : rentals.length === 0 ? (
              <div className="py-6 text-center text-xs text-gray-400">Активных аренд нет</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {rentals.map(r => (
                  <div key={r.id} className="p-3 flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-gray-900">
                          {r.bike?.code ?? '—'}
                        </span>
                        <span className="text-xs text-gray-500">{r.customer?.phone ?? '—'}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          hoursAgo(r.actualStartAt) > 24 ? 'bg-red-100 text-red-700'
                          : hoursAgo(r.actualStartAt) > 8 ? 'bg-orange-100 text-orange-700'
                          : 'bg-green-100 text-green-700'
                        }`}>
                          {hoursAgo(r.actualStartAt)}ч
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {r.pickupPoint?.name ?? '—'} · {r.pricingMode} · {r.basePrice ?? 0}₽ · {r.manager?.name ?? '—'}
                      </p>
                      <p className="text-xs text-gray-400">с {fmtDateTime(r.actualStartAt)}</p>
                    </div>
                    <button onClick={() => setForceClose(r)}
                      className="shrink-0 text-xs border border-red-300 text-red-600 px-2 py-1.5 rounded-xl hover:bg-red-50 font-medium">
                      Закрыть
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Stuck bikes (in_use) */}
          {!bikesLoading && stuckBikes.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-2xl overflow-hidden">
              <div className="p-4 border-b border-red-100">
                <h3 className="font-semibold text-red-800">Застрявшие велосипеды (в аренде без записи)</h3>
                <p className="text-xs text-red-600 mt-0.5">Статус «в аренде», но активной аренды нет — сбой данных</p>
              </div>
              <div className="divide-y divide-red-100">
                {stuckBikes.map(b => (
                  <div key={b.id} className="p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-sm font-semibold text-gray-900">{b.code}</span>
                        {b.model && <span className="text-xs text-gray-500 ml-1">{b.model}</span>}
                        {b.rentals.length > 0 && (
                          <p className="text-xs text-red-600">Есть активная аренда: {fmtDateTime(b.rentals[0]!.actualStartAt)}</p>
                        )}
                        {b.rentals.length === 0 && (
                          <p className="text-xs text-red-600">Активной аренды нет — велосипед завис</p>
                        )}
                      </div>
                      <span className="text-xs bg-red-200 text-red-800 px-2 py-0.5 rounded-full font-medium">в аренде</span>
                    </div>
                    <div className="flex gap-2">
                      <input
                        placeholder="Причина (необязательно)"
                        value={bikeStatusReason[b.id] ?? ''}
                        onChange={e => setBikeStatusReason(prev => ({ ...prev, [b.id]: e.target.value }))}
                        className="flex-1 text-xs border border-red-300 rounded-lg px-2 py-1.5"
                      />
                      <button
                        onClick={() => changeBikeStatus(b.id, 'free')}
                        disabled={bikeStatusChanging === b.id}
                        className="text-xs bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg font-medium">
                        {bikeStatusChanging === b.id ? '...' : 'Освободить'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* All bikes status panel */}
          <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <div>
                <h3 className="font-semibold text-gray-800">Все велосипеды</h3>
                <p className="text-xs text-gray-400 mt-0.5">Изменить статус любого велосипеда</p>
              </div>
              <button onClick={() => loadBikes(tenantId)}
                className="text-xs border border-gray-300 px-2 py-1 rounded-lg hover:bg-gray-50">
                Обновить
              </button>
            </div>
            {bikesLoading ? (
              <div className="py-6 text-center text-xs text-gray-400">Загрузка...</div>
            ) : otherBikes.length === 0 && stuckBikes.length === 0 ? (
              <div className="py-6 text-center text-xs text-gray-400">Велосипедов нет</div>
            ) : (
              <div className="divide-y divide-gray-100 max-h-[500px] overflow-y-auto">
                {otherBikes.map(b => (
                  <div key={b.id} className="p-3 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">{b.code}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          b.status === 'free' ? 'bg-green-100 text-green-700'
                          : b.status === 'reserved' ? 'bg-blue-100 text-blue-700'
                          : b.status === 'service' ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-red-100 text-red-700'
                        }`}>
                          {b.status === 'free' ? 'свободен' : b.status === 'reserved' ? 'резерв'
                          : b.status === 'service' ? 'сервис' : 'в аренде'}
                        </span>
                        {b.point && <span className="text-xs text-gray-400 truncate">{b.point.name}</span>}
                      </div>
                    </div>
                    <select
                      value={b.status}
                      onChange={e => changeBikeStatus(b.id, e.target.value)}
                      disabled={bikeStatusChanging === b.id}
                      className="text-xs border border-gray-300 rounded-lg px-2 py-1.5 bg-white disabled:opacity-50">
                      <option value="free">свободен</option>
                      <option value="reserved">резерв</option>
                      <option value="in_use">в аренде</option>
                      <option value="service">сервис</option>
                    </select>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {forceClose && (
        <ForceCloseModal
          rental={forceClose}
          onClose={() => setForceClose(null)}
          onDone={() => loadRentals(tenantId)}
        />
      )}
    </div>
  )
}

// ─── Database Tab ─────────────────────────────────────────────────────────────

type DbTable = 'bikes' | 'rentals' | 'shifts' | 'users' | 'payments'

interface DbBikeRow {
  id: string; tenantId: string; code: string; model: string | null; status: string; notes: string | null
  point: { name: string } | null; tariff: { name: string } | null
}
interface DbRentalRow {
  id: string; tenantId: string; status: string; pricingMode: string
  managerId: string; pickupPointId: string
  basePrice: number; totalPrice: number | null; prepaymentAmount: number
  actualStartAt: string; actualReturnAt: string | null
  managerNotes: string | null; bike: { code: string } | null
  customer: { phone: string } | null
  manager: { id: string; name: string } | null
  pickupPoint: { id: string; name: string } | null
}
interface DbShiftRow {
  id: string; tenantId: string; status: string; openedAt: string; closedAt: string | null
  notes: string | null; cashAtOpen: number | null
  manager: { id: string; name: string; phone: string; isActive: boolean } | null
  point: { id: string; name: string } | null
  _count?: { rentals: number }
}
interface DbUserRow {
  id: string; tenantId: string; name: string; phone: string; role: string
  isActive: boolean; lastLoginAt: string | null; createdAt: string
}

interface DbPaymentRow {
  id: string; tenantId: string; type: string; paymentMethod: string; amount: number
  note: string | null; createdAt: string
  rental: { id: string; bike: { code: string } | null } | null
  manager: { name: string } | null; point: { name: string } | null
}

type DbAnyRow = DbBikeRow | DbRentalRow | DbShiftRow | DbUserRow | DbPaymentRow

// Inline-editable cell: saves immediately on change/blur
function InlineSelect({ value, options, onSave, saving }: {
  value: string
  options: Array<{ value: string; label: string; color?: string }>
  onSave: (v: string) => Promise<void>
  saving?: boolean
}) {
  const [cur, setCur] = useState(value)
  const [busy, setBusy] = useState(false)

  const handle = async (v: string) => {
    if (v === cur) return
    setCur(v); setBusy(true)
    try { await onSave(v) } catch { setCur(cur) } finally { setBusy(false) }
  }

  const opt = options.find(o => o.value === cur)

  return (
    <div className="relative inline-flex items-center">
      <select
        value={cur}
        onChange={e => handle(e.target.value)}
        disabled={busy || saving}
        className={`text-xs border rounded-lg pl-2 pr-6 py-1 appearance-none bg-white font-medium cursor-pointer disabled:opacity-60
          ${opt?.color ?? 'border-gray-300 text-gray-700'}
          ${busy ? 'opacity-60' : 'hover:border-brand-400'}`}
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {busy && <span className="absolute right-1 text-[10px] text-gray-400 animate-pulse">…</span>}
    </div>
  )
}

function InlineText({ value, onSave, placeholder, mono }: {
  value: string; onSave: (v: string) => Promise<void>; placeholder?: string; mono?: boolean
}) {
  const [cur, setCur] = useState(value)
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState(false)

  const commit = async () => {
    if (cur === value) return
    setBusy(true)
    try { await onSave(cur); setSaved(true); setTimeout(() => setSaved(false), 1200) }
    catch { setCur(value) }
    finally { setBusy(false) }
  }

  return (
    <input
      value={cur}
      onChange={e => setCur(e.target.value)}
      onBlur={commit}
      onKeyDown={e => e.key === 'Enter' && (e.currentTarget.blur())}
      disabled={busy}
      placeholder={placeholder}
      className={`text-xs border rounded-lg px-2 py-1 w-full min-w-[80px] max-w-[180px] disabled:opacity-60
        ${saved ? 'border-green-400 bg-green-50' : 'border-gray-200 hover:border-brand-300 focus:border-brand-400'}
        ${mono ? 'font-mono' : ''}`}
    />
  )
}

function InlineToggle({ value, label, onSave }: {
  value: boolean; label?: [string, string]; onSave: (v: boolean) => Promise<void>
}) {
  const [cur, setCur] = useState(value)
  const [busy, setBusy] = useState(false)

  const toggle = async () => {
    const next = !cur
    setCur(next); setBusy(true)
    try { await onSave(next) } catch { setCur(cur) } finally { setBusy(false) }
  }

  const [on, off] = label ?? ['Вкл', 'Откл']
  return (
    <button
      onClick={toggle} disabled={busy}
      className={`text-xs px-2 py-1 rounded-lg font-medium border transition-colors disabled:opacity-50 ${
        cur ? 'bg-green-50 border-green-300 text-green-700' : 'bg-gray-100 border-gray-300 text-gray-500'
      }`}>
      {busy ? '…' : cur ? on : off}
    </button>
  )
}

const BIKE_STATUS_OPTS = [
  { value: 'free', label: 'свободен', color: 'border-green-300 text-green-700' },
  { value: 'reserved', label: 'резерв', color: 'border-blue-300 text-blue-700' },
  { value: 'in_use', label: 'в аренде', color: 'border-red-300 text-red-700' },
  { value: 'service', label: 'сервис', color: 'border-yellow-300 text-yellow-700' },
]
const RENTAL_STATUS_OPTS = [
  { value: 'active', label: 'активна', color: 'border-green-300 text-green-700' },
  { value: 'completed', label: 'завершена', color: 'border-gray-300 text-gray-600' },
  { value: 'emergency', label: 'аварийная', color: 'border-red-300 text-red-700' },
]
const SHIFT_STATUS_OPTS = [
  { value: 'open', label: 'открыта', color: 'border-green-300 text-green-700' },
  { value: 'closed', label: 'закрыта', color: 'border-gray-300 text-gray-600' },
]

function TH({ children }: { children: React.ReactNode }) {
  return <th className="text-left text-xs font-semibold text-gray-500 px-3 py-2 bg-gray-50 whitespace-nowrap">{children}</th>
}
function TD({ children, cls }: { children: React.ReactNode; cls?: string }) {
  return <td className={`px-3 py-2 text-sm align-middle ${cls ?? ''}`}>{children}</td>
}

function toDatetimeLocal(iso: string) {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

// ─── Rental Edit Modal ─────────────────────────────────────────────────────────

interface RentalEditForm {
  managerId: string; pickupPointId: string
  actualStartAt: string; actualReturnAt: string
  pricingMode: string; basePrice: string; totalPrice: string; prepaymentAmount: string
  status: string; managerNotes: string; reason: string
}

function RentalEditModal({ rental, tenantId, onClose, onSaved }: {
  rental: DbRentalRow; tenantId: string; onClose: () => void; onSaved: () => void
}) {
  const [managers, setManagers] = useState<{ id: string; name: string }[]>([])
  const [points, setPoints] = useState<{ id: string; name: string }[]>([])
  const [form, setForm] = useState<RentalEditForm>({
    managerId: rental.managerId ?? '',
    pickupPointId: rental.pickupPointId ?? '',
    actualStartAt: rental.actualStartAt ? toDatetimeLocal(rental.actualStartAt) : '',
    actualReturnAt: rental.actualReturnAt ? toDatetimeLocal(rental.actualReturnAt) : '',
    pricingMode: rental.pricingMode ?? 'hourly',
    basePrice: String(rental.basePrice ?? ''),
    totalPrice: String(rental.totalPrice ?? ''),
    prepaymentAmount: String(rental.prepaymentAmount ?? 0),
    status: rental.status,
    managerNotes: rental.managerNotes ?? '',
    reason: '',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    adminApi().get(`/admin/tenants/${tenantId}/users`)
      .then(r => setManagers((r.data.users as { id: string; name: string; role: string }[]).filter(u => u.role === 'manager')))
      .catch(() => null)
    adminApi().get(`/admin/tenants/${tenantId}/points`)
      .then(r => setPoints(r.data.points))
      .catch(() => null)
  }, [tenantId])

  const set = (key: keyof RentalEditForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm(f => ({ ...f, [key]: e.target.value }))

  const handleSave = async () => {
    if (!form.reason.trim()) { setError('Укажите причину изменения'); return }
    setSaving(true); setError(null)
    try {
      const body: Record<string, unknown> = { reason: form.reason, status: form.status, managerNotes: form.managerNotes }
      if (form.managerId) body.managerId = form.managerId
      if (form.pickupPointId) body.pickupPointId = form.pickupPointId
      if (form.actualStartAt) body.actualStartAt = new Date(form.actualStartAt).toISOString()
      if (form.actualReturnAt) body.actualReturnAt = new Date(form.actualReturnAt).toISOString()
      body.pricingMode = form.pricingMode
      if (form.basePrice !== '') body.basePrice = Number(form.basePrice)
      if (form.totalPrice !== '') body.totalPrice = Number(form.totalPrice)
      if (form.prepaymentAmount !== '') body.prepaymentAmount = Number(form.prepaymentAmount)
      await adminApi().patch(`/admin/db/rentals/${rental.id}`, body)
      onSaved(); onClose()
    } catch (err) {
      setError(apiError(err))
    } finally {
      setSaving(false)
    }
  }

  const inputCls = 'w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400'

  return (
    <Modal title={`Изменить аренду — ${rental.bike?.code ?? rental.id.slice(0, 8)}`} onClose={onClose}>
      <div className="space-y-3 text-sm">
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">Менеджер</label>
            <select value={form.managerId} onChange={set('managerId')} className={inputCls}>
              <option value="">— не менять —</option>
              {managers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">Точка</label>
            <select value={form.pickupPointId} onChange={set('pickupPointId')} className={inputCls}>
              <option value="">— не менять —</option>
              {points.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">Начало</label>
            <input type="datetime-local" value={form.actualStartAt} onChange={set('actualStartAt')} className={inputCls} />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">Конец</label>
            <input type="datetime-local" value={form.actualReturnAt} onChange={set('actualReturnAt')} className={inputCls} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">Тип тарифа</label>
            <select value={form.pricingMode} onChange={set('pricingMode')} className={inputCls}>
              <option value="hourly">почасовой</option>
              <option value="daily">посуточный</option>
              <option value="override">фиксированный</option>
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">Статус</label>
            <select value={form.status} onChange={set('status')} className={inputCls}>
              <option value="active">активна</option>
              <option value="completed">завершена</option>
              <option value="emergency">аварийная</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">База ₽</label>
            <input type="number" value={form.basePrice} onChange={set('basePrice')} className={inputCls} />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">Итого ₽</label>
            <input type="number" value={form.totalPrice} onChange={set('totalPrice')} className={inputCls} />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">Предоплата ₽</label>
            <input type="number" value={form.prepaymentAmount} onChange={set('prepaymentAmount')} className={inputCls} />
          </div>
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-gray-600">Заметки</label>
          <input type="text" value={form.managerNotes} onChange={set('managerNotes')} className={inputCls} />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-gray-600 text-red-600">Причина изменения *</label>
          <textarea value={form.reason} onChange={set('reason')} rows={2}
            placeholder="Исправление ошибки ввода, уточнение данных..."
            className={`${inputCls} resize-none`} />
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <button onClick={handleSave} disabled={saving}
          className="w-full bg-gray-900 text-white py-2.5 rounded-xl font-medium text-sm hover:bg-gray-700 disabled:opacity-50">
          {saving ? 'Сохранение...' : 'Сохранить изменения'}
        </button>
      </div>
    </Modal>
  )
}

// ─── Rental Create (Retro) Modal ───────────────────────────────────────────────

interface RentalCreateForm {
  customerPhone: string; bikeId: string; managerId: string; pickupPointId: string
  actualStartAt: string; actualReturnAt: string
  pricingMode: string; totalPrice: string; prepaymentAmount: string
  managerNotes: string; reason: string
}

function RentalCreateModal({ tenantId, onClose, onSaved }: {
  tenantId: string; onClose: () => void; onSaved: () => void
}) {
  const [managers, setManagers] = useState<{ id: string; name: string }[]>([])
  const [points, setPoints] = useState<{ id: string; name: string }[]>([])
  const [bikes, setBikes] = useState<{ id: string; code: string; model: string }[]>([])
  const [form, setForm] = useState<RentalCreateForm>({
    customerPhone: '', bikeId: '', managerId: '', pickupPointId: '',
    actualStartAt: '', actualReturnAt: '',
    pricingMode: 'override', totalPrice: '', prepaymentAmount: '0',
    managerNotes: '', reason: '',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    adminApi().get(`/admin/tenants/${tenantId}/users`)
      .then(r => setManagers((r.data.users as { id: string; name: string; role: string }[]).filter(u => u.role === 'manager')))
      .catch(() => null)
    adminApi().get(`/admin/tenants/${tenantId}/points`)
      .then(r => setPoints(r.data.points))
      .catch(() => null)
    adminApi().get(`/admin/tenants/${tenantId}/bikes`)
      .then(r => setBikes(r.data.bikes))
      .catch(() => null)
  }, [tenantId])

  const set = (key: keyof RentalCreateForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm(f => ({ ...f, [key]: e.target.value }))

  const handleSave = async () => {
    if (!form.reason.trim()) { setError('Укажите причину'); return }
    if (!form.customerPhone) { setError('Введите телефон клиента'); return }
    if (!form.managerId) { setError('Выберите менеджера'); return }
    if (!form.pickupPointId) { setError('Выберите точку'); return }
    if (!form.actualStartAt || !form.actualReturnAt) { setError('Укажите время начала и конца'); return }
    if (!form.totalPrice) { setError('Введите итоговую стоимость'); return }
    setSaving(true); setError(null)
    try {
      await adminApi().post('/admin/db/rentals', {
        tenantId,
        customerPhone: form.customerPhone,
        bikeId: form.bikeId || undefined,
        managerId: form.managerId,
        pickupPointId: form.pickupPointId,
        actualStartAt: new Date(form.actualStartAt).toISOString(),
        actualReturnAt: new Date(form.actualReturnAt).toISOString(),
        pricingMode: form.pricingMode,
        totalPrice: Number(form.totalPrice),
        prepaymentAmount: Number(form.prepaymentAmount) || 0,
        managerNotes: form.managerNotes || undefined,
        reason: form.reason,
      })
      onSaved(); onClose()
    } catch (err) {
      setError(apiError(err))
    } finally {
      setSaving(false)
    }
  }

  const inputCls = 'w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400'

  return (
    <Modal title="Создать аренду задним числом" onClose={onClose}>
      <div className="space-y-3 text-sm">
        <div className="space-y-1">
          <label className="text-xs font-medium text-gray-600">Телефон клиента</label>
          <input type="tel" value={form.customerPhone} onChange={set('customerPhone')}
            placeholder="+79001234567" className={inputCls} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">Менеджер *</label>
            <select value={form.managerId} onChange={set('managerId')} className={inputCls}>
              <option value="">Выберите...</option>
              {managers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">Точка *</label>
            <select value={form.pickupPointId} onChange={set('pickupPointId')} className={inputCls}>
              <option value="">Выберите...</option>
              {points.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-gray-600">Велосипед (необязательно)</label>
          <select value={form.bikeId} onChange={set('bikeId')} className={inputCls}>
            <option value="">— без велосипеда —</option>
            {bikes.map(b => <option key={b.id} value={b.id}>{b.code} {b.model}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">Начало *</label>
            <input type="datetime-local" value={form.actualStartAt} onChange={set('actualStartAt')} className={inputCls} />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">Конец *</label>
            <input type="datetime-local" value={form.actualReturnAt} onChange={set('actualReturnAt')} className={inputCls} />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">Тип</label>
            <select value={form.pricingMode} onChange={set('pricingMode')} className={inputCls}>
              <option value="override">фиксированный</option>
              <option value="hourly">почасовой</option>
              <option value="daily">посуточный</option>
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">Итого ₽ *</label>
            <input type="number" value={form.totalPrice} onChange={set('totalPrice')} className={inputCls} />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">Предоплата ₽</label>
            <input type="number" value={form.prepaymentAmount} onChange={set('prepaymentAmount')} className={inputCls} />
          </div>
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-gray-600">Заметки</label>
          <input type="text" value={form.managerNotes} onChange={set('managerNotes')} className={inputCls} />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-gray-600 text-red-600">Причина (зачем добавляем) *</label>
          <textarea value={form.reason} onChange={set('reason')} rows={2}
            placeholder="Аренда записана в тетрадь 28.03, не было интернета..."
            className={`${inputCls} resize-none`} />
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <button onClick={handleSave} disabled={saving}
          className="w-full bg-gray-900 text-white py-2.5 rounded-xl font-medium text-sm hover:bg-gray-700 disabled:opacity-50">
          {saving ? 'Создание...' : 'Создать аренду'}
        </button>
      </div>
    </Modal>
  )
}

function DatabaseTab({ tenants }: { tenants: Tenant[] }) {
  const [tenantId, setTenantId] = useState('')
  const [table, setTable] = useState<DbTable>('bikes')
  const [statusFilter, setStatusFilter] = useState('')
  const [items, setItems] = useState<DbAnyRow[]>([])
  const [total, setTotal] = useState(0)
  const [offset, setOffset] = useState(0)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [editRental, setEditRental] = useState<DbRentalRow | null>(null)
  const [showCreateRental, setShowCreateRental] = useState(false)
  const LIMIT = 50

  const load = (off = 0) => {
    setLoading(true); setLoadError(null)
    const params: Record<string, string | number> = { limit: LIMIT, offset: off }
    if (tenantId) params.tenantId = tenantId
    // each endpoint uses its own filter param name
    if (statusFilter) {
      if (table === 'users') params.role = statusFilter
      else if (table === 'payments') params.type = statusFilter
      else params.status = statusFilter
    }
    adminApi().get(`/admin/db/${table}`, { params })
      .then(r => {
        const data = r.data ?? {}
        setItems(Array.isArray(data.items) ? data.items : [])
        setTotal(typeof data.total === 'number' ? data.total : 0)
        setOffset(off)
      })
      .catch(err => { setItems([]); setTotal(0); setLoadError(apiError(err)) })
      .finally(() => setLoading(false))
  }

  useEffect(() => { load(0) }, [table, tenantId, statusFilter])

  const patch = async (endpoint: string, body: Record<string, unknown>) => {
    await adminApi().patch(endpoint, body)
    load(offset)
  }

  const statusOptions: Record<DbTable, Array<[string, string]>> = {
    bikes: [['', 'Все статусы'], ['free', 'свободные'], ['in_use', 'в аренде'], ['service', 'сервис'], ['reserved', 'резерв']],
    rentals: [['', 'Все статусы'], ['active', 'активные'], ['completed', 'завершённые'], ['emergency', 'аварийные']],
    shifts: [['', 'Все статусы'], ['open', 'открытые'], ['closed', 'закрытые']],
    users: [['', 'Все роли'], ['manager', 'менеджеры'], ['owner', 'владельцы']],
    payments: [['', 'Все типы'], ['prepayment', 'предоплата'], ['topup', 'доплата'], ['refund', 'возврат'], ['cashout', 'инкассация']],
  }

  const TABLE_LABELS: Record<DbTable, string> = {
    bikes: '🚲 Велосипеды', rentals: '📋 Аренды', shifts: '🔄 Смены', users: '👤 Менеджеры', payments: '💰 Платежи',
  }

  return (
    <div className="p-4 max-w-full mx-auto space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-semibold text-gray-700">База данных</h2>
        <p className="text-xs text-gray-400">Изменения применяются сразу — выбери тенанта</p>
      </div>

      {/* Filters bar */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="w-56">
          <TenantSelector tenants={tenants} value={tenantId} onChange={v => { setTenantId(v); setOffset(0) }} />
        </div>
        <div className="flex gap-1 flex-wrap">
          {(Object.entries(TABLE_LABELS) as [DbTable, string][]).map(([t, label]) => (
            <button key={t} onClick={() => { setTable(t); setStatusFilter(''); setOffset(0) }}
              className={`text-xs px-3 py-1.5 rounded-xl border font-medium transition-colors ${
                table === t ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}>
              {label}
            </button>
          ))}
        </div>
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setOffset(0) }}
          className="text-xs border border-gray-300 rounded-xl px-2 py-1.5 bg-white">
          {statusOptions[table].map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
        </select>
        <button onClick={() => load(offset)} className="text-xs border border-gray-300 px-3 py-1.5 rounded-xl hover:bg-gray-50">
          ↻ Обновить
        </button>
        {!loading && <span className="text-xs text-gray-400">всего: {total}</span>}
        {table === 'rentals' && tenantId && (
          <button onClick={() => setShowCreateRental(true)}
            className="text-xs px-3 py-1.5 rounded-xl bg-gray-900 text-white hover:bg-gray-700 font-medium">
            + Создать аренду
          </button>
        )}
      </div>

      {loadError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700 flex items-center gap-3">
          <span className="flex-1">{loadError}</span>
          <button onClick={() => load(offset)} className="text-xs border border-red-300 px-2 py-1 rounded-lg hover:bg-red-100">
            Повторить
          </button>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8 text-gray-400 text-sm">Загрузка...</div>
      ) : !loadError && items.length === 0 ? (
        <div className="text-center py-8 text-gray-400 text-sm">Записей нет</div>
      ) : !loadError && (
        <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                {table === 'bikes' && <>
                  <TH>Код</TH><TH>Статус</TH><TH>Модель</TH><TH>Точка</TH><TH>Тариф</TH><TH>Заметки</TH>
                </>}
                {table === 'rentals' && <>
                  <TH>Велик</TH><TH>Статус</TH><TH>Клиент</TH><TH>Начало</TH><TH>Тип</TH><TH>База</TH><TH>Итого ₽</TH><TH>Предоплата</TH><TH>Менеджер</TH><TH>Точка</TH><TH>Заметки</TH><TH>{''}</TH>
                </>}
                {table === 'shifts' && <>
                  <TH>Менеджер</TH><TH>Точка</TH><TH>Статус</TH><TH>Открыта</TH><TH>Закрыта</TH><TH>Аренд</TH><TH>Заметки</TH>
                </>}
                {table === 'users' && <>
                  <TH>Имя</TH><TH>Телефон</TH><TH>Роль</TH><TH>Активен</TH><TH>Последний вход</TH>
                </>}
                {table === 'payments' && <>
                  <TH>Дата</TH><TH>Тип</TH><TH>Метод</TH><TH>Сумма</TH><TH>Велик</TH><TH>Менеджер</TH><TH>Точка</TH><TH>Примечание</TH>
                </>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {table === 'bikes' && (items as DbBikeRow[]).map(b => (
                <tr key={b.id} className="hover:bg-gray-50 transition-colors">
                  <TD cls="font-mono font-semibold text-gray-900">{b.code}</TD>
                  <TD>
                    <InlineSelect
                      value={b.status}
                      options={BIKE_STATUS_OPTS}
                      onSave={v => patch(`/admin/db/bikes/${b.id}`, { status: v })}
                    />
                  </TD>
                  <TD>
                    <InlineText value={b.model ?? ''} placeholder="модель"
                      onSave={v => patch(`/admin/db/bikes/${b.id}`, { model: v })} />
                  </TD>
                  <TD cls="text-gray-500 text-xs">{b.point?.name ?? '—'}</TD>
                  <TD cls="text-gray-500 text-xs">{b.tariff?.name ?? '—'}</TD>
                  <TD>
                    <InlineText value={b.notes ?? ''} placeholder="заметки"
                      onSave={v => patch(`/admin/db/bikes/${b.id}`, { notes: v })} />
                  </TD>
                </tr>
              ))}

              {table === 'rentals' && (items as DbRentalRow[]).map(r => (
                <tr key={r.id} className="hover:bg-gray-50 transition-colors">
                  <TD cls="font-mono font-semibold">{r.bike?.code ?? '—'}</TD>
                  <TD>
                    <InlineSelect
                      value={r.status}
                      options={RENTAL_STATUS_OPTS}
                      onSave={v => patch(`/admin/db/rentals/${r.id}`, { status: v })}
                    />
                  </TD>
                  <TD cls="text-gray-500 font-mono text-xs">{r.customer?.phone ?? '—'}</TD>
                  <TD cls="text-gray-500 text-xs whitespace-nowrap">{fmtDateTime(r.actualStartAt)}</TD>
                  <TD cls="text-gray-400 text-xs">{r.pricingMode}</TD>
                  <TD cls="text-gray-700">{r.basePrice ?? 0}₽</TD>
                  <TD>
                    <InlineText value={String(r.totalPrice ?? '')} placeholder={String(r.basePrice ?? 0)} mono
                      onSave={v => patch(`/admin/db/rentals/${r.id}`, { totalPrice: Number(v) })} />
                  </TD>
                  <TD cls="text-gray-500 font-mono text-xs">{r.prepaymentAmount ?? 0}₽</TD>
                  <TD cls="text-gray-500 text-xs">{r.manager?.name ?? '—'}</TD>
                  <TD cls="text-gray-500 text-xs">{r.pickupPoint?.name ?? '—'}</TD>
                  <TD>
                    <InlineText value={r.managerNotes ?? ''} placeholder="заметки"
                      onSave={v => patch(`/admin/db/rentals/${r.id}`, { managerNotes: v })} />
                  </TD>
                  <TD>
                    <button onClick={() => setEditRental(r)}
                      className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1 rounded-lg hover:bg-blue-50">
                      ✏️
                    </button>
                  </TD>
                </tr>
              ))}

              {table === 'shifts' && (items as DbShiftRow[]).map(s => (
                <tr key={s.id} className="hover:bg-gray-50 transition-colors">
                  <TD cls="font-medium">{s.manager?.name ?? '—'}
                    {s.manager && !s.manager.isActive && <span className="ml-1 text-xs text-red-400">(откл)</span>}
                  </TD>
                  <TD cls="text-gray-500 text-xs">{s.point?.name ?? '—'}</TD>
                  <TD>
                    <InlineSelect
                      value={s.status}
                      options={SHIFT_STATUS_OPTS}
                      onSave={v => patch(`/admin/db/shifts/${s.id}`, { status: v })}
                    />
                  </TD>
                  <TD cls="text-gray-500 text-xs whitespace-nowrap">{fmtDateTime(s.openedAt)}</TD>
                  <TD cls="text-gray-500 text-xs whitespace-nowrap">{s.closedAt ? fmtDateTime(s.closedAt) : '—'}</TD>
                  <TD cls="text-gray-700 text-center">{s._count?.rentals ?? 0}</TD>
                  <TD>
                    <InlineText value={s.notes ?? ''} placeholder="заметки"
                      onSave={v => patch(`/admin/db/shifts/${s.id}`, { notes: v })} />
                  </TD>
                </tr>
              ))}

              {table === 'users' && (items as DbUserRow[]).map(u => (
                <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                  <TD cls="font-medium">
                    <InlineText value={u.name} onSave={v => patch(`/admin/db/users/${u.id}`, { name: v })} />
                  </TD>
                  <TD cls="font-mono text-xs text-gray-600">{u.phone}</TD>
                  <TD>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      u.role === 'owner' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {u.role === 'owner' ? 'владелец' : 'менеджер'}
                    </span>
                  </TD>
                  <TD>
                    <InlineToggle value={u.isActive} label={['активен', 'откл']}
                      onSave={v => patch(`/admin/db/users/${u.id}`, { isActive: v })} />
                  </TD>
                  <TD cls="text-gray-400 text-xs">{u.lastLoginAt ? fmtDate(u.lastLoginAt) : 'никогда'}</TD>
                </tr>
              ))}

              {table === 'payments' && (items as DbPaymentRow[]).map(p => {
                const PAYMENT_TYPE_LABELS: Record<string, { label: string; color: string }> = {
                  prepayment: { label: 'предоплата', color: 'bg-blue-100 text-blue-700' },
                  topup:      { label: 'доплата',    color: 'bg-yellow-100 text-yellow-700' },
                  refund:     { label: 'возврат',    color: 'bg-red-100 text-red-600' },
                  cashout:    { label: 'инкассация', color: 'bg-gray-100 text-gray-600' },
                }
                const PAYMENT_METHOD_LABELS: Record<string, string> = {
                  cash: '💵 нал', card: '💳 карта', transfer: '🔄 перевод',
                }
                const typeInfo = PAYMENT_TYPE_LABELS[p.type] ?? { label: p.type, color: 'bg-gray-100 text-gray-600' }
                return (
                  <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                    <TD cls="text-gray-500 text-xs whitespace-nowrap">{fmtDateTime(p.createdAt)}</TD>
                    <TD>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${typeInfo.color}`}>
                        {typeInfo.label}
                      </span>
                    </TD>
                    <TD cls="text-xs text-gray-600">{PAYMENT_METHOD_LABELS[p.paymentMethod] ?? p.paymentMethod}</TD>
                    <TD cls={`font-mono font-semibold ${p.type === 'refund' ? 'text-red-600' : 'text-green-700'}`}>
                      <InlineText value={String(p.amount ?? 0)} mono
                        onSave={v => patch(`/admin/db/payments/${p.id}`, { amount: Number(v) })} />
                    </TD>
                    <TD cls="font-mono text-xs text-gray-500">{p.rental?.bike?.code ?? '—'}</TD>
                    <TD cls="text-xs text-gray-500">{p.manager?.name ?? '—'}</TD>
                    <TD cls="text-xs text-gray-500">{p.point?.name ?? '—'}</TD>
                    <TD>
                      <InlineText value={p.note ?? ''} placeholder="примечание"
                        onSave={v => patch(`/admin/db/payments/${p.id}`, { note: v })} />
                    </TD>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {total > LIMIT && (
        <div className="flex items-center justify-between text-xs text-gray-400">
          <button disabled={offset === 0} onClick={() => load(Math.max(0, offset - LIMIT))}
            className="border border-gray-300 px-3 py-1.5 rounded-lg hover:bg-gray-50 disabled:opacity-40">
            ← Назад
          </button>
          <span>{offset + 1}–{Math.min(offset + LIMIT, total)} из {total}</span>
          <button disabled={offset + LIMIT >= total} onClick={() => load(offset + LIMIT)}
            className="border border-gray-300 px-3 py-1.5 rounded-lg hover:bg-gray-50 disabled:opacity-40">
            Вперёд →
          </button>
        </div>
      )}

      {editRental && (
        <RentalEditModal
          rental={editRental}
          tenantId={editRental.tenantId}
          onClose={() => setEditRental(null)}
          onSaved={() => load(offset)}
        />
      )}
      {showCreateRental && tenantId && (
        <RentalCreateModal
          tenantId={tenantId}
          onClose={() => setShowCreateRental(false)}
          onSaved={() => load(0)}
        />
      )}
    </div>
  )
}

// ─── Dashboard ────────────────────────────────────────────────────────────────

type ActiveModal = { type: TenantAction; tenant: Tenant } | null
type AdminTab = 'overview' | 'tenants' | 'incidents' | 'audit' | 'commands' | 'operations' | 'database'

function AdminDashboard({ onLogout }: { onLogout: () => void }) {
  const [tab, setTab] = useState<AdminTab>('overview')
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [showCreateDemo, setShowCreateDemo] = useState(false)
  const [modal, setModal] = useState<ActiveModal>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<TenantStatus | ''>('')

  const load = () => {
    setLoading(true); setError(null)
    adminApi().get('/admin/tenants')
      .then(r => setTenants(r.data.tenants))
      .catch(err => setError(apiError(err)))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [])

  const closeModal = () => setModal(null)

  const filteredTenants = tenants.filter(t => {
    if (statusFilter && t.status !== statusFilter) return false
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      const owner = t.users[0]
      return (
        t.name.toLowerCase().includes(q) ||
        t.id.toLowerCase().includes(q) ||
        owner?.name.toLowerCase().includes(q) ||
        owner?.phone.includes(q)
      )
    }
    return true
  })

  const tabs: { key: AdminTab; label: string }[] = [
    { key: 'overview', label: 'Обзор' },
    { key: 'tenants', label: `Тенанты${!loading ? ` (${tenants.length})` : ''}` },
    { key: 'operations', label: 'Операции' },
    { key: 'database', label: 'База данных' },
    { key: 'incidents', label: 'Инциденты' },
    { key: 'audit', label: 'Аудит' },
    { key: 'commands', label: 'Диагностика' },
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xl">🔧</span>
          <span className="font-bold text-gray-900">VeloRent Admin</span>
        </div>
        <button onClick={onLogout} className="text-xs text-gray-500 border border-gray-300 px-3 py-1.5 rounded-lg hover:bg-gray-50">
          Выйти
        </button>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 px-4 flex gap-4 overflow-x-auto">
        {tabs.map(({ key, label }) => (
          <button key={key} onClick={() => setTab(key)}
            className={`py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              tab === key ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-400 hover:text-gray-600'
            }`}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <DashboardTab />}

      {tab === 'operations' && <OperationsTab tenants={tenants} />}

      {tab === 'database' && <DatabaseTab tenants={tenants} />}

      {tab === 'incidents' && <IncidentsTab />}

      {tab === 'audit' && <AuditLogTab />}

      {tab === 'commands' && <DiagnosticsTab />}

      {tab === 'tenants' && (
        <div className="p-4 max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-gray-700">Список тенантов</h2>
            <div className="flex gap-2">
              <button onClick={load} className="text-xs border border-gray-300 px-3 py-1.5 rounded-lg hover:bg-gray-50">
                Обновить
              </button>
              <button onClick={() => setShowCreateDemo(true)}
                className="text-xs bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded-lg">
                + Демо
              </button>
              <button onClick={() => setShowCreate(true)}
                className="text-xs bg-gray-900 text-white px-3 py-1.5 rounded-lg hover:bg-gray-800">
                + Создать
              </button>
            </div>
          </div>

          {/* Search + status filter */}
          <div className="mb-3 space-y-2">
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Поиск по названию, телефону, ID..."
              className="w-full rounded-xl border-gray-300 text-sm"
            />
            <div className="flex gap-1.5 flex-wrap">
              {([['', 'Все'], ['active', 'Активные'], ['trial', 'Пробные'], ['paused', 'На паузе'], ['blocked', 'Заблокированные']] as [TenantStatus | '', string][]).map(([val, label]) => (
                <button key={val} onClick={() => setStatusFilter(val)}
                  className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                    statusFilter === val
                      ? 'bg-gray-900 text-white border-gray-900'
                      : 'border-gray-300 text-gray-500 hover:bg-gray-50'
                  }`}>
                  {label}
                  {val !== '' && !loading && (
                    <span className="ml-1 opacity-60">
                      ({tenants.filter(t => t.status === val).length})
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {error && <div className="mb-4 bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">{error}</div>}

          {loading ? (
            <div className="text-center py-12 text-gray-400 text-sm">Загрузка...</div>
          ) : filteredTenants.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">
              {tenants.length === 0 ? 'Тенантов нет' : 'Ничего не найдено'}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredTenants.map(t => (
                <TenantCard key={t.id} tenant={t} onAction={(action, tenant) => setModal({ type: action, tenant })} onToggled={load} />
              ))}
            </div>
          )}
        </div>
      )}

      {showCreate && <CreateTenantModal onClose={() => setShowCreate(false)} onCreated={load} />}
      {showCreateDemo && <CreateDemoTenantModal onClose={() => setShowCreateDemo(false)} onCreated={load} />}
      {modal?.type === 'edit' && <EditOwnerModal tenant={modal.tenant} onClose={closeModal} onSaved={load} />}
      {modal?.type === 'temp-access' && <TempAccessModal tenant={modal.tenant} onClose={closeModal} />}
      {modal?.type === 'limits' && <EditLimitsModal tenant={modal.tenant} onClose={closeModal} onSaved={load} />}
      {modal?.type === 'delete' && <DeleteTenantModal tenant={modal.tenant} onClose={closeModal} onDeleted={() => { closeModal(); load() }} />}
    </div>
  )
}

// ─── Root ─────────────────────────────────────────────────────────────────────

export default function AdminPage() {
  const [authed, setAuthed] = useState(() => !!sessionStorage.getItem(SESSION_KEY))

  const handleLogout = () => {
    sessionStorage.removeItem(SESSION_KEY)
    setAuthed(false)
  }

  if (!authed) return <LoginScreen onLogin={() => setAuthed(true)} />
  return <AdminDashboard onLogout={handleLogout} />
}
